/**
 * Settings (Options) scene — full rewrite.
 *
 * Light-themed, Tailwind-styled, page-based UI. One page per game plus a
 * "General" page for cross-game options (language, leaderboards, visual
 * filters, advanced import/export/reset).
 *
 * Persistence model — IMPORTANT:
 *   • Every control auto-writes to localStorage on every change (via
 *     `theme.setSetting` / `theme.set`). The running game picks the new
 *     value up the moment its scene boots, no "Apply" step required.
 *   • The single primary "Save" button in the top bar POSTs the same data
 *     to `/__save-preset` on the dev server so it lands in `data/preset.js`.
 *     The next `npm run build` / `npm run build:offline` bakes those values
 *     into the offline game so a clean install on another machine ships
 *     with the curated settings as defaults.
 *
 * Dev-only: the scene is stripped from production builds by
 * `__PRODUCTION_BUILD__` define + esbuild DCE (see src/main.js).
 * Tailwind is loaded lazily from `vendor/tailwind.js` only when this scene
 * mounts, so it never ships to the offline build.
 */

import { Graphics } from '../../vendor/pixi.min.mjs';
import { BaseScene } from './BaseScene.js';
import { t, setLang } from '../config/i18n.js';
import { setParticleQuality } from '../games/platformer/ParticleSystem.js';

/* ─────────────────────────────────────────────────────────────────────
   SLOT DESCRIPTORS — what gets edited per game
   ───────────────────────────────────────────────────────────────────── */

function imageSlot(key, label, options = {}) {
    return {
        key,
        label,
        kind: 'skin',
        assetType: options.assetType || 'image',
        mappingType: options.mappingType || 'single',
        category: options.category || 'misc',
        footprint: options.footprint || { w: 1, h: 1 },
        supportsDirection: !!options.supportsDirection,
        supportsAnimation: !!options.supportsAnimation,
        animationType: options.animationType || null,
        fitMode: options.fitMode || 'contain',
        notes: options.notes || '',
    };
}

/**
 * Per-game declaration: id = game id used by SkinResolver,
 * title = sidebar label, slots = list of editable image slots
 * (one settingsSlot is implied — handled separately by the Gameplay tab).
 */
const GAMES = [
    {
        id: 'frogger',
        title: 'Balloon',
        slots: [
            imageSlot('frogger.atlas.main', 'Shared Atlas Source', { category: 'atlas', fitMode: 'contain' }),
            imageSlot('frogger.background', 'Background', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('frogger.sky', 'Sky Layer', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('frogger.bottom', 'Start Area Background', { category: 'board', fitMode: 'cover' }),
            imageSlot('frogger.side.panel', 'Side Panel / Border', { category: 'ui', fitMode: 'cover' }),
            imageSlot('frogger.lane.road', 'Road Texture', { category: 'board', fitMode: 'cover' }),
            imageSlot('frogger.lane.safe', 'Safe Lane Texture', { category: 'board', fitMode: 'cover' }),
            imageSlot('frogger.lane.marking', 'Road Markings', { category: 'board', fitMode: 'cover' }),
            imageSlot('frogger.decor.tree', 'Safe: Tree', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.decor.pinetree', 'Safe: Pine Tree', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.decor.bench', 'Safe: Bench', { category: 'obstacles', footprint: { w: 2, h: 1 }, supportsAnimation: true }),
            imageSlot('frogger.decor.lamp', 'Safe: Lamp Post', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.decor.flowers', 'Safe: Flowers', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.decor.grass', 'Safe: Grass Tuft', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.decor.bush', 'Safe: Bush', { category: 'obstacles', supportsAnimation: true }),
            imageSlot('frogger.collectible.coin', 'Coin Icon', { category: 'pickups', supportsAnimation: true }),
            imageSlot('frogger.collectible.star', 'Star Icon', { category: 'pickups', supportsAnimation: true }),
            imageSlot('frogger.ability.dash', 'Ability: Dash', { category: 'pickups', supportsAnimation: true }),
            imageSlot('frogger.ability.slow', 'Ability: Slow Time', { category: 'pickups', supportsAnimation: true }),
            imageSlot('frogger.ability.bomb', 'Ability: Bomb', { category: 'pickups', supportsAnimation: true }),
            imageSlot('frogger.finish.building', 'Finish Building', { category: 'goal', footprint: { w: 3, h: 2 } }),
            imageSlot('frogger.finish.gate', 'Finish Gate', { category: 'goal', footprint: { w: 3, h: 1 } }),
            imageSlot('frogger.player', 'Balloon Player', { category: 'characters', supportsAnimation: true, supportsDirection: true, animationType: 'sheet' }),
            imageSlot('frogger.vehicle.bike', 'Bike', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('frogger.vehicle.car', 'Car', { category: 'characters', footprint: { w: 3, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('frogger.vehicle.truck', 'Truck', { category: 'characters', footprint: { w: 4, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('frogger.vehicle.bus', 'Tram / Bus', { category: 'characters', footprint: { w: 5, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('frogger.bird', 'Bird Enemy', { category: 'characters', supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('frogger.hud.panel', 'HUD Panel', { category: 'ui', fitMode: 'cover' }),
            imageSlot('frogger.hud.life', 'HUD Life Icon', { category: 'ui' }),
            imageSlot('frogger.hud.timer', 'HUD Timer Icon', { category: 'ui' }),
            imageSlot('frogger.hud.score', 'HUD Score Icon', { category: 'ui' }),
            imageSlot('frogger.hud.row', 'HUD Row Icon', { category: 'ui' }),
            imageSlot('frogger.controls', 'On-Screen Controls', { category: 'ui', fitMode: 'contain' }),
        ],
    },
    {
        id: 'snake',
        title: 'Tram',
        slots: [
            imageSlot('snake.atlas.main', 'Shared Atlas Source', { category: 'atlas', fitMode: 'contain' }),
            imageSlot('snake.background', 'Background', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('snake.board.cell', 'Board Cell Texture', { category: 'board', fitMode: 'cover' }),
            imageSlot('snake.board.border', 'Board Border', { category: 'board', fitMode: 'cover' }),
            imageSlot('snake.sky.cloud.large.1', 'Cloud Large 1 (4x2)', { category: 'board', footprint: { w: 4, h: 2 } }),
            imageSlot('snake.sky.cloud.large.2', 'Cloud Large 2 (4x2)', { category: 'board', footprint: { w: 4, h: 2 } }),
            imageSlot('snake.sky.cloud.small.1', 'Cloud Small 1 (2x1)', { category: 'board', footprint: { w: 2, h: 1 } }),
            imageSlot('snake.sky.cloud.small.2', 'Cloud Small 2 (2x1)', { category: 'board', footprint: { w: 2, h: 1 } }),
            imageSlot('snake.tram.head', 'Tram First Car', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('snake.tram.body', 'Tram Secondary Car', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('snake.tram.connector', 'Tram Connector', { category: 'characters', supportsAnimation: true }),
            imageSlot('snake.track.straight', 'Track Straight', { category: 'board' }),
            imageSlot('snake.track.corner', 'Track Corner', { category: 'board', supportsDirection: true }),
            imageSlot('snake.food', 'Food / Pickup', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.station.single', 'Station Single (4x2)', { category: 'pickups', footprint: { w: 4, h: 2 }, supportsAnimation: true }),
            imageSlot('snake.station.wide', 'Station Double (4x2)', { category: 'pickups', footprint: { w: 4, h: 2 }, supportsAnimation: true }),
            imageSlot('snake.station.tall', 'Station Tall (legacy)', { category: 'pickups', footprint: { w: 1, h: 2 }, supportsAnimation: true }),
            imageSlot('snake.exit', 'Exit Gate', { category: 'goal', footprint: { w: 3, h: 1 } }),
            imageSlot('snake.door', 'Adventure Door', { category: 'goal', footprint: { w: 3, h: 1 } }),
            imageSlot('snake.house', 'Final House', { category: 'goal', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.obstacle.tree.single', 'Small Tree (2x2)', { category: 'obstacles', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.obstacle.tree.pine', 'Pine Tree (1x2)', { category: 'obstacles', footprint: { w: 1, h: 2 } }),
            imageSlot('snake.obstacle.bush.single', 'Green Bush (2x2)', { category: 'obstacles', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.obstacle.bush.pink', 'Pink Bush (4x2)', { category: 'obstacles', footprint: { w: 4, h: 2 } }),
            imageSlot('snake.obstacle.bush.yellow', 'Yellow Bush (1x1)', { category: 'obstacles', footprint: { w: 1, h: 1 } }),
            imageSlot('snake.obstacle.rock.single', 'Small Stone (2x2)', { category: 'obstacles', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.obstacle.rock.large', 'Boulder (3x2)', { category: 'obstacles', footprint: { w: 3, h: 2 } }),
            imageSlot('snake.obstacle.flowerbed', 'Flowerbed (2x1)', { category: 'obstacles', footprint: { w: 2, h: 1 } }),
            imageSlot('snake.obstacle.flowerbed.orange', 'Orange Flowerbed (1x1)', { category: 'obstacles', footprint: { w: 1, h: 1 } }),
            imageSlot('snake.obstacle.lamp', 'Street Lamp (1x2)', { category: 'obstacles', footprint: { w: 1, h: 2 } }),
            imageSlot('snake.obstacle.traffic_lamp', 'Traffic Light (1x2)', { category: 'obstacles', footprint: { w: 1, h: 2 } }),
            imageSlot('snake.obstacle.bench', 'Park Bench (2x1)', { category: 'obstacles', footprint: { w: 2, h: 1 } }),
            imageSlot('snake.obstacle.barrier', 'Barrier (3x2)', { category: 'obstacles', footprint: { w: 3, h: 2 } }),
            imageSlot('snake.obstacle.stop', 'Stop Sign (2x2)', { category: 'obstacles', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.obstacle.icecream_truck', 'Ice-cream Truck (2x3)', { category: 'obstacles', footprint: { w: 2, h: 3 } }),
            imageSlot('snake.obstacle.building.large', 'Generic Building (3x3)', { category: 'obstacles', footprint: { w: 3, h: 3 } }),
            imageSlot('snake.obstacle.building.hospital', 'Hospital (4x4)', { category: 'obstacles', footprint: { w: 4, h: 4 } }),
            imageSlot('snake.obstacle.building.pink', 'Pink Building (4x4)', { category: 'obstacles', footprint: { w: 4, h: 4 } }),
            imageSlot('snake.obstacle.building.modern', 'Modern Building (3x2)', { category: 'obstacles', footprint: { w: 3, h: 2 } }),
            imageSlot('snake.obstacle.building.restaurant', 'Restaurant (3x2)', { category: 'obstacles', footprint: { w: 3, h: 2 } }),
            imageSlot('snake.obstacle.building.vegies', 'Veggie Stand (2x2)', { category: 'obstacles', footprint: { w: 2, h: 2 } }),
            imageSlot('snake.perk.shrink', 'Perk: Shrink', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.perk.speed_up', 'Perk: Speed Up', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.perk.random_turn', 'Perk: Chaos', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.perk.ghost', 'Perk: Ghost', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.perk.slow', 'Perk: Slow Mo', { category: 'pickups', supportsAnimation: true }),
            imageSlot('snake.hud.panel', 'HUD Panel', { category: 'ui' }),
            imageSlot('snake.hud.timer', 'HUD Timer Icon', { category: 'ui' }),
            imageSlot('snake.hud.score', 'HUD Score Icon (Cup)', { category: 'ui' }),
            imageSlot('snake.hud.cars', 'HUD Cars Icon (Tram)', { category: 'ui' }),
            imageSlot('snake.hud.board', 'HUD Screen Icon', { category: 'ui' }),
        ],
    },
    {
        id: 'pipeline',
        title: 'Track Builder',
        slots: [
            imageSlot('pipeline.atlas.main', 'Shared Atlas Source', { category: 'atlas', fitMode: 'contain' }),
            imageSlot('pipeline.background', 'Background', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('pipeline.board.cell', 'Board Cell Texture', { category: 'board' }),
            imageSlot('pipeline.board.border', 'Board Border', { category: 'board', fitMode: 'cover' }),
            imageSlot('pipeline.track.straight', 'Track Straight', { category: 'board' }),
            imageSlot('pipeline.track.corner', 'Track Corner', { category: 'board', supportsDirection: true }),
            imageSlot('pipeline.track.t', 'Track T Junction', { category: 'board', supportsDirection: true }),
            imageSlot('pipeline.track.cross', 'Track Cross', { category: 'board' }),
            imageSlot('pipeline.track.deadend', 'Track Dead End', { category: 'board', supportsDirection: true }),
            imageSlot('pipeline.tram.head', 'Tram First Carriage', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('pipeline.tram.body', 'Tram Other Carriage', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('pipeline.tram.connector', 'Tram Connector', { category: 'characters' }),
            imageSlot('pipeline.station', 'Station Marker', { category: 'goal' }),
            imageSlot('pipeline.goal', 'Goal / House', { category: 'goal', footprint: { w: 2, h: 2 } }),
            imageSlot('pipeline.start', 'Start Marker', { category: 'goal' }),
            imageSlot('pipeline.blocked', 'Blocked Tile', { category: 'obstacles' }),
            imageSlot('pipeline.hud.panel', 'HUD Panel', { category: 'ui' }),
        ],
    },
    {
        id: 'trainsim',
        title: 'Train Simulator',
        slots: [
            imageSlot('trainsim.atlas.main', 'Shared Atlas Source', { category: 'atlas', fitMode: 'contain' }),
            imageSlot('trainsim.background', 'Background', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('trainsim.board.cell', 'Board Cell Texture', { category: 'board' }),
            imageSlot('trainsim.board.border', 'Board Border', { category: 'board', fitMode: 'cover' }),
            imageSlot('trainsim.track.straight', 'Track Straight', { category: 'board' }),
            imageSlot('trainsim.track.corner', 'Track Corner', { category: 'board', supportsDirection: true }),
            imageSlot('trainsim.track.t', 'Track T Junction', { category: 'board', supportsDirection: true }),
            imageSlot('trainsim.track.cross', 'Track Cross', { category: 'board' }),
            imageSlot('trainsim.track.deadend', 'Track Dead End', { category: 'board', supportsDirection: true }),
            imageSlot('trainsim.tram.head', 'Tram First Carriage', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('trainsim.tram.body', 'Tram Other Carriage', { category: 'characters', footprint: { w: 2, h: 1 }, supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('trainsim.tram.connector', 'Tram Connector', { category: 'characters' }),
            imageSlot('trainsim.tile.lamp', 'Signal Lamp', { category: 'goal' }),
            imageSlot('trainsim.tile.switch', 'Track Switch', { category: 'goal' }),
            imageSlot('trainsim.spawn.t1', 'Train 1 Spawn — Keleti',         { category: 'goal', footprint: { w: 8, h: 4 } }),
            imageSlot('trainsim.spawn.t2', 'Train 2 Spawn — Nyugati',        { category: 'goal', footprint: { w: 8, h: 4 } }),
            imageSlot('trainsim.spawn.t3', 'Train 3 Spawn — Szombathely',    { category: 'goal', footprint: { w: 8, h: 4 } }),
            imageSlot('trainsim.spawn.t4', 'Train 4 Spawn — Szolnok',        { category: 'goal', footprint: { w: 8, h: 4 } }),
            imageSlot('trainsim.spawn.t5', 'Train 5 Spawn — Miskolc-Tiszai', { category: 'goal', footprint: { w: 8, h: 4 } }),
            imageSlot('trainsim.goal.t1', 'Train 1 Goal (Red)',    { category: 'goal' }),
            imageSlot('trainsim.goal.t2', 'Train 2 Goal (Blue)',   { category: 'goal' }),
            imageSlot('trainsim.goal.t3', 'Train 3 Goal (Yellow)', { category: 'goal' }),
            imageSlot('trainsim.goal.t4', 'Train 4 Goal (Green)',  { category: 'goal' }),
            imageSlot('trainsim.goal.t5', 'Train 5 Goal (Purple)', { category: 'goal' }),
            imageSlot('trainsim.station', 'Station Marker', { category: 'goal' }),
            imageSlot('trainsim.station.people.large', 'Station People (Large)', { category: 'characters' }),
            imageSlot('trainsim.station.people.small', 'Station People (Small)', { category: 'characters' }),
            imageSlot('trainsim.hud.panel', 'HUD Panel', { category: 'ui' }),
        ],
    },
    {
        id: 'platformer',
        title: 'Platformer',
        slots: [
            imageSlot('platformer.atlas.main', 'Shared Atlas Source', { category: 'atlas', fitMode: 'contain' }),
            imageSlot('platformer.background', 'Sky / Background', { category: 'board', fitMode: 'cover', supportsAnimation: true, animationType: 'video-or-sheet' }),
            imageSlot('platformer.tile.ground', 'Ground', { category: 'board' }),
            imageSlot('platformer.tile.brick', 'Brick', { category: 'board' }),
            imageSlot('platformer.tile.block.hard', 'Hard Block', { category: 'board' }),
            imageSlot('platformer.tile.ice', 'Ice (Slippery)', { category: 'board' }),
            imageSlot('platformer.tile.sand', 'Sand (Grippy)', { category: 'board' }),
            imageSlot('platformer.tile.block.question', 'Question Block', { category: 'board', supportsAnimation: true }),
            imageSlot('platformer.tile.block.coin', 'Coin Block', { category: 'board', supportsAnimation: true }),
            imageSlot('platformer.tile.pipe.top.left', 'Pipe Top-Left', { category: 'board' }),
            imageSlot('platformer.tile.pipe.top.right', 'Pipe Top-Right', { category: 'board' }),
            imageSlot('platformer.tile.pipe.body.left', 'Pipe Body-Left', { category: 'board' }),
            imageSlot('platformer.tile.pipe.body.right', 'Pipe Body-Right', { category: 'board' }),
            imageSlot('platformer.decor.hill.large', 'Hill (Large)', { category: 'obstacles', footprint: { w: 4, h: 2 } }),
            imageSlot('platformer.decor.hill.small', 'Hill (Small)', { category: 'obstacles', footprint: { w: 3, h: 1 } }),
            imageSlot('platformer.decor.cloud.1', 'Cloud 1', { category: 'obstacles', footprint: { w: 3, h: 1 } }),
            imageSlot('platformer.decor.cloud.2', 'Cloud 2', { category: 'obstacles', footprint: { w: 2, h: 1 } }),
            imageSlot('platformer.decor.bush.1', 'Bush 1', { category: 'obstacles', footprint: { w: 2, h: 1 } }),
            imageSlot('platformer.decor.bush.2', 'Bush 2', { category: 'obstacles', footprint: { w: 3, h: 1 } }),
            imageSlot('platformer.decor.castle', 'Castle', { category: 'goal', footprint: { w: 4, h: 4 } }),
            imageSlot('platformer.decor.pole.flag', 'Flag Pole', { category: 'goal', footprint: { w: 1, h: 8 } }),
            imageSlot('platformer.entity.player.spawn', 'Player', { category: 'characters', supportsDirection: true, supportsAnimation: true, animationType: 'sheet' }),
            imageSlot('platformer.entity.goomba', 'Goomba', { category: 'characters', supportsAnimation: true }),
            imageSlot('platformer.entity.koopa', 'Koopa', { category: 'characters', supportsAnimation: true }),
            imageSlot('platformer.entity.mushroom', 'Mushroom', { category: 'pickups', supportsAnimation: true }),
            imageSlot('platformer.entity.coin', 'Coin', { category: 'pickups', supportsAnimation: true }),
            imageSlot('platformer.entity.flag.goal', 'Flag (Goal)', { category: 'goal' }),
            imageSlot('platformer.entity.checkpoint', 'Checkpoint', { category: 'goal' }),
        ],
    },
];

const CATEGORY_META = {
    board:      { label: 'Board & Background',    icon: 'dashboard' },
    characters: { label: 'Characters & Vehicles', icon: 'directions_car' },
    obstacles:  { label: 'Obstacles & Decor',     icon: 'park' },
    pickups:    { label: 'Pickups & Powers',      icon: 'deployed_code' },
    goal:       { label: 'Goals & Gates',         icon: 'flag' },
    ui:         { label: 'HUD & UI',              icon: 'tv_options_edit_channels' },
    misc:       { label: 'Other',                 icon: 'category' },
};

/* ─────────────────────────────────────────────────────────────────────
   TAILWIND BOOTSTRAP
   Load the vendored Tailwind Play CDN once, configure preflight OFF so
   Tailwind's global resets don't bleed into the menu / game DOM
   overlays after the user leaves Settings.
   ───────────────────────────────────────────────────────────────────── */

let _tailwindLoad = null;

function ensureTailwind() {
    if (_tailwindLoad) return _tailwindLoad;
    _tailwindLoad = new Promise((resolve) => {
        const apply = () => {
            try {
                if (window.tailwind?.config !== undefined) {
                    window.tailwind.config = {
                        corePlugins: { preflight: false },
                        theme: {
                            extend: {
                                colors: {
                                    brand: {
                                        50:  '#eff8ff',
                                        100: '#dbeefe',
                                        200: '#bfe1fd',
                                        300: '#93cdfb',
                                        400: '#60aff6',
                                        500: '#3b8df1',
                                        600: '#256fe5',
                                        700: '#1d59cd',
                                        800: '#1d48a4',
                                        900: '#1e3f82',
                                    },
                                },
                                fontFamily: {
                                    sans: ['Poppins', 'Inter', 'Segoe UI', 'Arial', 'sans-serif'],
                                },
                            },
                        },
                    };
                }
            } catch (err) {
                console.warn('[Options] tailwind config failed:', err);
            }
            resolve();
        };
        if (window.tailwind) { apply(); return; }
        const existing = document.querySelector('script[data-tailwind-vendor]');
        if (existing) { existing.addEventListener('load', apply, { once: true }); return; }
        const s = document.createElement('script');
        s.src = './vendor/tailwind.js';
        s.dataset.tailwindVendor = '1';
        s.onload = apply;
        s.onerror = () => { console.warn('[Options] tailwind.js failed to load'); resolve(); };
        document.head.appendChild(s);
    });
    return _tailwindLoad;
}

/* ─────────────────────────────────────────────────────────────────────
   ONE-OFF SCENE STYLES — small block of supplemental rules Tailwind
   doesn't cover (range thumb styling, scoped reset so our buttons
   aren't blue-and-underlined while Tailwind preflight is off).
   ───────────────────────────────────────────────────────────────────── */

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    _stylesInjected = true;
    const css = `
    .opt-root, .opt-root * { box-sizing: border-box; }
    /* Zero-specificity reset (:where) so Tailwind's single-class utilities
       (px-3, bg-brand-600, …) always win — a normal '.opt-root button'
       selector would out-specify them and strip every button's styling. */
    :where(.opt-root) :where(button) { background: transparent; border: none;
        padding: 0; margin: 0; font-family: inherit; color: inherit;
        cursor: pointer; line-height: inherit; text-align: inherit; }
    :where(.opt-root) :where(input, select, textarea) {
        font-family: inherit; color: inherit; }
    .opt-root .material-symbols-rounded { font-size: 18px; line-height: 1; vertical-align: middle; }
    /* Active sidebar entry: fill the icon chip with the brand colour. */
    .opt-root .is-active .opt-nav-ico { background: #256fe5; color: #fff; }
    .opt-root [type="number"]::-webkit-outer-spin-button,
    .opt-root [type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .opt-root [type="number"] { -moz-appearance: textfield; }
    .opt-root input[type="range"] { -webkit-appearance: none; appearance: none;
        height: 6px; background: linear-gradient(to right, #3b8df1, #93cdfb);
        border-radius: 4px; outline: none; }
    .opt-root input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none;
        appearance: none; width: 16px; height: 16px; border-radius: 50%;
        background: #fff; border: 2px solid #3b8df1; cursor: pointer;
        box-shadow: 0 1px 2px rgba(15,23,42,0.25); }
    .opt-root input[type="range"]::-moz-range-thumb { width: 14px; height: 14px;
        border-radius: 50%; background: #fff; border: 2px solid #3b8df1;
        cursor: pointer; box-shadow: 0 1px 2px rgba(15,23,42,0.25); }
    .opt-toast { animation: opt-toast-in 0.18s ease-out; }
    @keyframes opt-toast-in {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    `;
    const style = document.createElement('style');
    style.dataset.optsStyles = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

/* ─────────────────────────────────────────────────────────────────────
   THE SCENE
   ───────────────────────────────────────────────────────────────────── */

export class OptionsScene extends BaseScene {
    _root = null;
    _unsubscribe = null;
    _activePage = 'general';      // 'general' | game id
    _activeGameTab = 'gameplay';  // 'gameplay' | 'sprites'
    _atlasSelection = null;       // current cell selection in the atlas card
    _content = null;              // main scrollable content host
    _sidebar = null;              // sidebar nav container (for active highlight)
    _toastEl = null;

    init(game) {
        super.init(game);
        this._createCanvasBackdrop();
        injectStyles();
        ensureTailwind().then(() => this._mountDom());
    }

    destroy() {
        if (this._unsubscribe) { this._unsubscribe(); this._unsubscribe = null; }
        if (this._root?.parentNode) this._root.parentNode.removeChild(this._root);
        this._root = null;
        this._content = null;
        this._sidebar = null;
        this._toastEl = null;
        super.destroy();
    }

    /**
     * Soft cyan/white fill on the Pixi canvas behind the DOM overlay so
     * brief flashes during scene transitions don't show a black bar.
     */
    _createCanvasBackdrop() {
        const bg = new Graphics();
        bg.rect(0, 0, this.game.width, this.game.height);
        bg.fill(0xf1f5f9);
        this.container.addChild(bg);
    }

    /* ─────────────────────────────────────────────────────────────
       DOM SHELL — top bar + sidebar + content host
       ───────────────────────────────────────────────────────────── */

    _mountDom() {
        const root = document.createElement('div');
        root.className = 'opt-root fixed inset-0 z-50 overflow-hidden ' +
            'bg-gradient-to-br from-slate-50 via-white to-sky-50 ' +
            'text-slate-800 font-sans';
        this._root = root;

        root.appendChild(this._buildTopBar());

        const layout = document.createElement('div');
        layout.className = 'flex h-[calc(100%-64px)]';
        root.appendChild(layout);

        this._sidebar = this._buildSidebar();
        layout.appendChild(this._sidebar);

        const main = document.createElement('main');
        main.className = 'flex-1 overflow-y-auto';
        layout.appendChild(main);

        this._content = document.createElement('div');
        this._content.className = 'max-w-5xl mx-auto px-8 py-8 space-y-6';
        main.appendChild(this._content);

        // Toast container — sits in the top-bar gutter so it never covers
        // the Save button itself while it's animating.
        this._toastEl = document.createElement('div');
        this._toastEl.className = 'fixed top-20 right-8 z-[60] pointer-events-none';
        root.appendChild(this._toastEl);

        this._unsubscribe = this.game.theme.subscribe(() => {
            // Only the sprite-tab cares about live skin updates (preview
            // thumbs, atlas grid). Gameplay controls already reflect
            // their own writes locally, so we don't need to re-render on
            // every setting toggle (that would cause flicker).
            if (this._activePage !== 'general' && this._activeGameTab === 'sprites') {
                this._renderContent();
            }
        });

        this._renderContent();
        document.body.appendChild(root);
    }

    /* ─────────────────────────────────────────────────────────────
       TOP BAR — title, Save (primary), Back
       ───────────────────────────────────────────────────────────── */

    _buildTopBar() {
        const bar = document.createElement('header');
        bar.className = 'h-16 flex items-center justify-between gap-4 ' +
            'px-6 bg-white/85 backdrop-blur border-b border-slate-200 shadow-sm';

        const left = document.createElement('div');
        left.className = 'flex items-center gap-3 min-w-0';
        left.innerHTML = `
            <span class="material-symbols-rounded text-brand-600" style="font-size:26px">tune</span>
            <div class="flex flex-col min-w-0">
                <span class="text-base font-bold tracking-tight truncate">
                    ${this._esc(t('theme.title') || 'Settings')}
                </span>
                <span class="text-[11px] text-slate-500 truncate">
                    ${this._esc(t('settings.changes_autosave'))}
                </span>
            </div>
        `;
        bar.appendChild(left);

        const right = document.createElement('div');
        right.className = 'flex items-center gap-2';

        const save = this._primaryButton('save', t('theme.save_preset'), () => this._savePreset());
        right.appendChild(save);

        const back = this._ghostButton('arrow_back', t('common.back'), () => {
            this.game.sceneManager.switchScene('menu');
        });
        right.appendChild(back);

        bar.appendChild(right);
        return bar;
    }

    /* ─────────────────────────────────────────────────────────────
       SIDEBAR — General + one entry per game
       ───────────────────────────────────────────────────────────── */

    _buildSidebar() {
        const aside = document.createElement('aside');
        aside.className = 'w-72 shrink-0 bg-white/80 border-r border-slate-200 ' +
            'overflow-y-auto py-8 px-5';

        // Section heading.
        const heading = document.createElement('div');
        heading.className = 'px-2 mb-4 text-[11px] font-bold uppercase ' +
            'tracking-[0.14em] text-slate-400';
        heading.textContent = t('settings.nav_general') === 'General' ? 'Pages' : 'Oldalak';
        aside.appendChild(heading);

        const items = [
            { id: 'general', label: t('settings.nav_general'), icon: 'tune', sub: this._sidebarSub('general') },
            ...GAMES.map((g) => ({
                id: g.id, label: g.title, icon: this._gameIcon(g.id), sub: this._sidebarSub(g.id),
            })),
        ];

        const list = document.createElement('nav');
        list.className = 'flex flex-col gap-2';
        aside.appendChild(list);

        for (const it of items) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.dataset.page = it.id;
            btn.className = this._navButtonClass(it.id === this._activePage);
            btn.innerHTML = `
                <span class="opt-nav-ico flex items-center justify-center h-9 w-9 shrink-0 rounded-xl bg-slate-100 text-brand-600 transition-colors">
                    <span class="material-symbols-rounded" style="font-size:20px">${it.icon}</span>
                </span>
                <span class="flex flex-col min-w-0 leading-tight">
                    <span class="text-sm font-bold truncate">${this._esc(it.label)}</span>
                    <span class="text-[11px] text-slate-400 truncate">${this._esc(it.sub)}</span>
                </span>
            `;
            btn.addEventListener('click', () => this._switchPage(it.id));
            list.appendChild(btn);
        }

        return aside;
    }

    /** One-line descriptor shown under each sidebar entry. */
    _sidebarSub(id) {
        if (id === 'general') {
            return t('settings.nav_general') === 'General'
                ? 'Language · filters · advanced' : 'Nyelv · szűrők · haladó';
        }
        return t('settings.tab_gameplay') === 'Gameplay'
            ? 'Gameplay & sprites' : 'Játékmenet & sprite-ok';
    }

    _gameIcon(id) {
        switch (id) {
            case 'frogger':    return 'travel';
            case 'snake':      return 'tram';
            case 'pipeline':   return 'route';
            case 'trainsim':   return 'train';
            case 'platformer': return 'sports_esports';
            default:           return 'sports_esports';
        }
    }

    _navButtonClass(active) {
        return [
            'group flex items-center gap-3 w-full text-left px-3 py-3 rounded-2xl',
            'transition-colors',
            active
                ? 'is-active bg-brand-50 text-brand-800 ring-1 ring-brand-200 shadow-sm'
                : 'text-slate-700 hover:bg-slate-100',
        ].join(' ');
    }

    _switchPage(id) {
        if (id === this._activePage) return;
        this._activePage = id;
        // Reset per-game sub-tab to Gameplay so the heavy Sprites
        // tab doesn't surprise the user when they click a game in
        // the sidebar.
        this._activeGameTab = 'gameplay';
        // Update sidebar highlight without re-rendering the whole bar.
        if (this._sidebar) {
            const btns = this._sidebar.querySelectorAll('button[data-page]');
            btns.forEach((b) => {
                b.className = this._navButtonClass(b.dataset.page === id);
            });
        }
        this._renderContent();
    }

    /* ─────────────────────────────────────────────────────────────
       CONTENT ROUTER
       ───────────────────────────────────────────────────────────── */

    _renderContent() {
        if (!this._content) return;
        this._content.innerHTML = '';
        this._content.scrollTop = 0;

        if (this._activePage === 'general') {
            this._renderGeneralPage();
            return;
        }
        const game = GAMES.find((g) => g.id === this._activePage);
        if (game) this._renderGamePage(game);
    }

    /* ─────────────────────────────────────────────────────────────
       GENERAL PAGE
       ───────────────────────────────────────────────────────────── */

    _renderGeneralPage() {
        this._content.appendChild(this._pageHeader(
            t('settings.nav_general'),
            t('settings.subtitle_light'),
            'tune',
        ));

        // ── Language ─────────────────────────────────────────────
        const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
            ? globalThis.__BUILD_LANG_MODE__
            : 'both';
        if (buildLangMode === 'both') {
            const langCard = this._card();
            langCard.appendChild(this._cardHeader('translate',
                t('settings.available_languages'),
                t('settings.available_languages_hint')));
            const body = this._cardBody();
            body.appendChild(this._buildLanguageBar());
            langCard.appendChild(body);
            this._content.appendChild(langCard);
        }

        // ── Leaderboards ────────────────────────────────────────
        const lbBuildEnabled = typeof globalThis.__BUILD_LEADERBOARDS__ !== 'undefined'
            ? globalThis.__BUILD_LEADERBOARDS__
            : true;
        if (lbBuildEnabled) {
            const lbCard = this._card();
            lbCard.appendChild(this._cardHeader('leaderboard',
                t('settings.leaderboards_enabled'),
                t('settings.leaderboards_hint')));
            const body = this._cardBody();
            body.appendChild(this._toggle('shared.settings', 'leaderboardsEnabled',
                t('settings.leaderboards_enabled'), true));
            lbCard.appendChild(body);
            this._content.appendChild(lbCard);
        }

        // ── Performance (particle quality / device tier) ─────────
        this._content.appendChild(this._buildPerformanceCard());

        // ── Visual Filters ───────────────────────────────────────
        this._content.appendChild(this._buildFiltersCard());

        // ── Advanced (collapsed) ─────────────────────────────────
        this._content.appendChild(this._buildAdvancedCard());
    }

    /* ─────────────────────────────────────────────────────────────
       PERFORMANCE — particle-quality device tier
       ───────────────────────────────────────────────────────────── */

    _buildPerformanceCard() {
        const card = this._card();
        card.appendChild(this._cardHeader('speed',
            t('settings.particle_quality'),
            t('settings.particle_quality_hint')));
        const body = this._cardBody();

        const slotKey = 'shared.settings';
        const settingKey = 'particleQuality';
        const def = this.game.theme?.getSettingSchema?.('shared', settingKey)?.default || 'high';
        const cur = this.game.theme.getSetting(slotKey, settingKey, def);
        const options = [
            { id: 'low',    label: t('settings.pq_low') },
            { id: 'medium', label: t('settings.pq_medium') },
            { id: 'high',   label: t('settings.pq_high') },
            { id: 'ultra',  label: t('settings.pq_ultra') },
        ];
        // Segmented picker — persists the choice AND applies the new ceiling
        // live, so the effect is visible without a reload.
        body.appendChild(this._segmented(options, cur, (id) => {
            this.game.theme.setSetting(slotKey, settingKey, id);
            setParticleQuality(id);
        }));
        card.appendChild(body);
        return card;
    }

    /* ─────────────────────────────────────────────────────────────
       LANGUAGE BAR
       ───────────────────────────────────────────────────────────── */

    _buildLanguageBar() {
        const slotKey = 'shared.settings';
        const settingKey = 'availableLanguages';
        const defaultValue = 'both';
        const options = [
            { id: 'both', label: t('settings.lang_both') },
            { id: 'hu',   label: t('settings.lang_hu_only') },
            { id: 'en',   label: t('settings.lang_en_only') },
        ];
        const cur = this.game.theme.getSetting(slotKey, settingKey, defaultValue);
        const bar = this._segmented(options, cur, (id) => {
            this.game.theme.setSetting(slotKey, settingKey, id);
            if (id === 'hu' || id === 'en') {
                this.game.theme.setLang(id);
                setLang(id);
            }
            // Tear down and re-init so every translated label refreshes.
            this.destroy();
            this.init(this.game);
        });
        return bar;
    }

    /* ─────────────────────────────────────────────────────────────
       VISUAL FILTERS — toggle row + per-filter param blocks
       ───────────────────────────────────────────────────────────── */

    _buildFiltersCard() {
        const card = this._card();
        card.appendChild(this._cardHeader('filter_b_and_w',
            t('settings.visual_filters'),
            t('settings.visual_filters_hint')));
        const body = this._cardBody();

        const key = 'shared.settings';
        const schema = (k) => this.game.theme?.getSettingSchema?.('shared', k) || {};

        const toggleGrid = document.createElement('div');
        toggleGrid.className = 'grid grid-cols-2 md:grid-cols-3 gap-2';

        const customizeHost = document.createElement('div');
        customizeHost.className = 'space-y-3 mt-4';
        const renderCustomize = () => this._renderFilterParams(customizeHost);

        const filterKeys = [
            ['crtFilter',        'filter_crt'],
            ['oldFilmFilter',    'filter_oldfilm'],
            ['pixelateFilter',   'filter_pixelate'],
            ['tiltShiftFilter',  'filter_tiltshift'],
            ['godRaysFilter',    'filter_godrays'],
            ['adjustmentFilter', 'filter_adjustment'],
            ['reflectionFilter', 'filter_reflection'],
            ['greyscaleFilter',  'filter_greyscale'],
        ];
        for (const [skey, labelKey] of filterKeys) {
            toggleGrid.appendChild(this._toggleCallback(key, skey,
                t(`settings.${labelKey}`),
                !!schema(skey).default, renderCustomize));
        }

        body.appendChild(toggleGrid);
        body.appendChild(customizeHost);
        renderCustomize();

        card.appendChild(body);
        return card;
    }

    _renderFilterParams(host) {
        host.innerHTML = '';
        const key = 'shared.settings';
        const get = (k) => this.game.theme.getSetting(key, k, false);
        const sch = (k) => this.game.theme?.getSettingSchema?.('shared', k) || {};

        const SECTIONS = [
            { flag: 'crtFilter', title: t('settings.filter_crt'), icon: 'tv',
              params: [
                  ['crtAnimating',     t('settings.animating')],
                  ['crtVerticalLine',  t('settings.crt_vertical_line')],
                  ['crtCurvature',     t('settings.crt_curvature')],
                  ['crtScanline',      t('settings.crt_scanline')],
                  ['crtLineWidth',     t('settings.crt_line_width')],
                  ['crtChroma',        t('settings.crt_chroma')],
                  ['crtNoise',         t('settings.crt_noise')],
                  ['crtNoiseSize',     t('settings.crt_noise_size')],
                  ['crtVignette',      t('settings.crt_vignette')],
                  ['crtVignetteAlpha', t('settings.crt_vignette_alpha')],
                  ['crtSeed',          t('settings.crt_seed')],
              ],
            },
            { flag: 'oldFilmFilter', title: t('settings.filter_oldfilm'), icon: 'theaters',
              params: [
                  ['oldFilmAnimating', t('settings.animating')],
                  ['oldFilmSepia',     t('settings.oldfilm_sepia')],
                  ['oldFilmNoise',     t('settings.oldfilm_noise')],
                  ['oldFilmScratch',   t('settings.oldfilm_scratch')],
                  ['oldFilmVignette',  t('settings.oldfilm_vignette')],
              ],
            },
            { flag: 'pixelateFilter', title: t('settings.filter_pixelate'), icon: 'grid_view',
              params: [ ['pixelateSize', t('settings.pixelate_size')] ],
            },
            { flag: 'tiltShiftFilter', title: t('settings.filter_tiltshift'), icon: 'center_focus_weak',
              params: [
                  ['tiltShiftBand',  t('settings.tiltshift_band')],
                  ['tiltShiftRange', t('settings.tiltshift_range')],
                  ['tiltShiftBlur',  t('settings.tiltshift_blur')],
              ],
            },
            { flag: 'godRaysFilter', title: t('settings.filter_godrays'), icon: 'wb_sunny',
              params: [
                  ['godRaysAnimating', t('settings.animating')],
                  ['godRaysIntensity', t('settings.godrays_intensity')],
                  ['godRaysExposure',  t('settings.godrays_exposure')],
                  ['godRaysDecay',     t('settings.godrays_decay')],
                  ['godRaysLightX',    t('settings.godrays_light_x')],
                  ['godRaysLightY',    t('settings.godrays_light_y')],
              ],
            },
            { flag: 'adjustmentFilter', title: t('settings.filter_adjustment'), icon: 'tune',
              params: [
                  ['adjustGamma',      t('settings.adjust_gamma')],
                  ['adjustSaturation', t('settings.adjust_saturation')],
                  ['adjustContrast',   t('settings.adjust_contrast')],
                  ['adjustBrightness', t('settings.adjust_brightness')],
                  ['adjustRed',        t('settings.adjust_red')],
                  ['adjustGreen',      t('settings.adjust_green')],
                  ['adjustBlue',       t('settings.adjust_blue')],
              ],
            },
            { flag: 'reflectionFilter', title: t('settings.filter_reflection'), icon: 'water',
              params: [
                  ['reflectionAnimating', t('settings.animating')],
                  ['reflectBoundary',     t('settings.reflect_boundary')],
                  ['reflectAmplitude',    t('settings.reflect_amplitude')],
                  ['reflectWaveLength',   t('settings.reflect_wavelength')],
                  ['reflectAlpha',        t('settings.reflect_alpha')],
                  ['reflectSpeed',        t('settings.reflect_speed')],
              ],
            },
            { flag: 'greyscaleFilter', title: t('settings.filter_greyscale'), icon: 'invert_colors',
              params: [
                  ['greyscaleAmount', t('settings.greyscale_amount')],
                  ['greyscaleTintR',  t('settings.greyscale_tint_r')],
                  ['greyscaleTintG',  t('settings.greyscale_tint_g')],
                  ['greyscaleTintB',  t('settings.greyscale_tint_b')],
              ],
            },
        ];

        let anyEnabled = false;
        for (const s of SECTIONS) {
            if (!get(s.flag)) continue;
            anyEnabled = true;

            const block = document.createElement('div');
            block.className = 'rounded-xl border border-brand-100 bg-brand-50/60 p-4';

            const head = document.createElement('div');
            head.className = 'flex items-center gap-2 mb-3';
            head.innerHTML = `
                <span class="material-symbols-rounded text-brand-700">${s.icon}</span>
                <span class="text-sm font-bold text-brand-900">${this._esc(s.title)}</span>
            `;
            block.appendChild(head);

            const grid = document.createElement('div');
            grid.className = 'grid grid-cols-1 md:grid-cols-2 gap-3';

            for (const [pkey, plabel] of s.params) {
                const ps = sch(pkey);
                if (ps.type === 'boolean') {
                    grid.appendChild(this._toggle(key, pkey, plabel, !!ps.default));
                } else {
                    grid.appendChild(this._slider(key, pkey, plabel,
                        ps.default ?? 0,
                        ps.min ?? 0,
                        ps.max ?? 1,
                        ps.step ?? 0.05,
                    ));
                }
            }
            block.appendChild(grid);
            host.appendChild(block);
        }

        if (!anyEnabled) {
            const hint = document.createElement('div');
            hint.className = 'text-center text-xs text-slate-500 italic ' +
                'rounded-xl border border-dashed border-slate-200 bg-slate-50 py-4';
            hint.textContent = t('settings.visual_filters_empty');
            host.appendChild(hint);
        }
    }

    /* ─────────────────────────────────────────────────────────────
       ADVANCED (collapsed by default) — import / export / reset
       ───────────────────────────────────────────────────────────── */

    _buildAdvancedCard() {
        const card = this._card();

        const details = document.createElement('details');
        details.className = 'group';
        card.appendChild(details);

        const summary = document.createElement('summary');
        summary.className = 'flex items-center gap-3 cursor-pointer select-none ' +
            'list-none px-5 py-4 marker:hidden';
        summary.innerHTML = `
            <span class="material-symbols-rounded text-slate-500">build</span>
            <div class="flex-1 min-w-0">
                <div class="text-sm font-bold text-slate-800">
                    ${this._esc(t('settings.advanced'))}
                </div>
                <div class="text-xs text-slate-500 truncate">
                    ${this._esc(t('settings.advanced_hint'))}
                </div>
            </div>
            <span class="material-symbols-rounded text-slate-400 group-open:rotate-180 transition-transform">expand_more</span>
        `;
        details.appendChild(summary);

        const body = document.createElement('div');
        body.className = 'px-5 pb-5 pt-1 flex flex-wrap gap-2 border-t border-slate-100';

        body.appendChild(this._secondaryButton('download',
            t('theme.export'), () => this._exportConfig()));
        body.appendChild(this._secondaryButton('rocket_launch',
            t('theme.export_preset'), () => this._exportPreset()));

        // Import (label wraps a hidden file input so it inherits all
        // button styles without us having to write a custom uploader).
        const importLabel = document.createElement('label');
        importLabel.className = this._buttonClass('secondary');
        importLabel.style.cursor = 'pointer';
        importLabel.innerHTML = `
            <span class="material-symbols-rounded">upload</span>
            <span>${this._esc(t('theme.import'))}</span>
        `;
        const importInput = document.createElement('input');
        importInput.type = 'file';
        importInput.accept = '.json';
        importInput.style.display = 'none';
        importInput.addEventListener('change', (e) => this._importConfig(e));
        importLabel.appendChild(importInput);
        body.appendChild(importLabel);

        body.appendChild(this._dangerButton('restart_alt',
            t('theme.reset'), () => {
                if (!window.confirm(t('theme.reset_confirm'))) return;
                this.game.theme.reset();
                this._renderContent();
            }));

        details.appendChild(body);
        return card;
    }

    /* ─────────────────────────────────────────────────────────────
       PER-GAME PAGE — sub-tabs (Gameplay / Sprites)
       ───────────────────────────────────────────────────────────── */

    _renderGamePage(game) {
        this._content.appendChild(this._pageHeader(
            game.title,
            t('settings.subtitle_light'),
            this._gameIcon(game.id),
        ));

        // Sub-tab bar
        const tabs = document.createElement('div');
        tabs.className = 'inline-flex p-1 bg-white rounded-xl ring-1 ring-slate-200 shadow-sm';
        const mkTab = (id, label, icon) => {
            const b = document.createElement('button');
            b.type = 'button';
            const active = this._activeGameTab === id;
            b.className = [
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold',
                active
                    ? 'bg-brand-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100',
            ].join(' ');
            b.innerHTML = `
                <span class="material-symbols-rounded">${icon}</span>
                <span>${this._esc(label)}</span>
            `;
            b.addEventListener('click', () => {
                if (this._activeGameTab === id) return;
                this._activeGameTab = id;
                this._renderContent();
            });
            return b;
        };
        tabs.appendChild(mkTab('gameplay', t('settings.tab_gameplay'), 'sports_esports'));
        tabs.appendChild(mkTab('sprites',  t('settings.tab_sprites'),  'image'));
        this._content.appendChild(tabs);

        if (this._activeGameTab === 'gameplay') {
            this._renderGameplayTab(game);
        } else {
            this._renderSpritesTab(game);
        }
    }

    /* ─────────────────────────────────────────────────────────────
       GAMEPLAY TAB — game-specific settings + Play / Stage Editor
       ───────────────────────────────────────────────────────────── */

    _renderGameplayTab(game) {
        const card = this._card();
        card.appendChild(this._cardHeader('sports_esports',
            `${game.title} · ${t('settings.tab_gameplay')}`,
            t('settings.changes_autosave')));

        const body = this._cardBody();

        switch (game.id) {
            case 'frogger':    this._buildFroggerSettings(body, 'frogger.settings'); break;
            case 'snake':      this._buildSnakeSettings(body, 'snake.settings'); break;
            case 'pipeline':   this._buildPipelineSettings(body, 'pipeline.settings'); break;
            case 'trainsim':   this._buildTrainsimSettings(body, 'trainsim.settings'); break;
            case 'platformer': this._buildPlatformerSettings(body, 'platformer.settings'); break;
        }

        // Per-game action row: Play + (Stage Editor, where supported).
        const actions = document.createElement('div');
        actions.className = 'flex flex-wrap gap-2 pt-4 mt-2 border-t border-slate-100';
        actions.appendChild(this._primaryButton('play_arrow', t('common.play'), () => {
            this.game.sceneManager.switchScene(game.id);
        }));
        if (game.id === 'trainsim' || game.id === 'platformer') {
            actions.appendChild(this._secondaryButton('edit_square',
                t('theme.stage_editor'), () => {
                    try { localStorage.setItem('mini-games-editor', '1'); } catch (e) { /* ok */ }
                    this._launchStageEditor(game.id);
                }));
        }
        body.appendChild(actions);

        card.appendChild(body);
        this._content.appendChild(card);
    }

    /* ─────────────────────────────────────────────────────────────
       SPRITES TAB — atlas card + category sections of slot cards
       ───────────────────────────────────────────────────────────── */

    _renderSpritesTab(game) {
        const atlasSlot = game.slots.find((s) => s.category === 'atlas');
        const skinSlots = game.slots.filter((s) => s.category !== 'atlas');

        const atlasKey = `${game.id}.atlas.main`;
        if (atlasSlot) {
            this._content.appendChild(this._buildAtlasCard(atlasSlot, atlasKey));
        }

        const grouped = {};
        for (const s of skinSlots) (grouped[s.category || 'misc'] ??= []).push(s);

        const order = ['board', 'characters', 'obstacles', 'pickups', 'goal', 'ui', 'misc'];
        for (const cat of order) {
            const slots = grouped[cat];
            if (!slots) continue;
            const meta = CATEGORY_META[cat] || CATEGORY_META.misc;
            const card = this._card();
            card.appendChild(this._cardHeader(meta.icon, meta.label, ''));
            const body = this._cardBody();
            const grid = document.createElement('div');
            grid.className = 'grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
            for (const s of slots) grid.appendChild(this._buildSlotCard(s, atlasKey));
            body.appendChild(grid);
            card.appendChild(body);
            this._content.appendChild(card);
        }
    }

    /* ─────────────────────────────────────────────────────────────
       ATLAS MASTER CARD
       Upload a spritesheet, set grid size, drag-select cells. The
       active selection (col/row/span) is stashed in `_atlasSelection`
       so any slot's "From Atlas" button picks it up.
       ───────────────────────────────────────────────────────────── */

    _buildAtlasCard(slot, atlasKey) {
        const card = this._card();
        card.appendChild(this._cardHeader('grid_view', 'Spritesheet Atlas',
            'Upload the sheet, set its grid, then drag-select a region.'));
        const body = this._cardBody();

        const src = this.game.theme.get(atlasKey);
        const desc = this.game.theme.getDescriptor?.(atlasKey) || {};
        const atlasGrid = desc.atlas || { gridCols: 4, gridRows: 4 };

        // Upload row.
        const uploadRow = document.createElement('div');
        uploadRow.className = 'flex flex-wrap items-center gap-2';
        uploadRow.appendChild(this._uploadButton(slot));
        if (src) {
            uploadRow.appendChild(this._secondaryButton('close', 'Clear',
                () => this.game.theme.clear(atlasKey)));
        }
        body.appendChild(uploadRow);

        if (!src) {
            const empty = document.createElement('div');
            empty.className = 'rounded-xl border-2 border-dashed border-slate-200 ' +
                'bg-slate-50 text-slate-400 text-sm font-medium ' +
                'flex items-center justify-center h-36 mt-3';
            empty.textContent = 'No spritesheet uploaded yet';
            body.appendChild(empty);
            card.appendChild(body);
            return card;
        }

        // Grid-size strip.
        const gridConfig = document.createElement('div');
        gridConfig.className = 'flex flex-wrap items-center gap-4 px-4 py-3 ' +
            'rounded-xl bg-brand-50 ring-1 ring-brand-100 mt-3';
        const lbl = document.createElement('span');
        lbl.className = 'text-xs font-bold uppercase tracking-wider text-brand-800';
        lbl.textContent = 'Grid Size';
        gridConfig.appendChild(lbl);

        gridConfig.appendChild(this._stepper('Columns', atlasGrid.gridCols || 4, 1, 128, (v) => {
            this._setAtlasGrid(atlasKey, desc, { ...atlasGrid, gridCols: v });
        }));
        const sep = document.createElement('span');
        sep.className = 'text-slate-400 font-bold';
        sep.textContent = '×';
        gridConfig.appendChild(sep);
        gridConfig.appendChild(this._stepper('Rows', atlasGrid.gridRows || 4, 1, 128, (v) => {
            this._setAtlasGrid(atlasKey, desc, { ...atlasGrid, gridRows: v });
        }));

        const count = document.createElement('span');
        count.className = 'ml-auto text-xs font-semibold text-brand-700';
        count.textContent = `= ${(atlasGrid.gridCols || 4) * (atlasGrid.gridRows || 4)} cells`;
        gridConfig.appendChild(count);
        body.appendChild(gridConfig);

        // Drag-select picker.
        const cols = Math.max(1, atlasGrid.gridCols || 4);
        const rows = Math.max(1, atlasGrid.gridRows || 4);

        const outer = document.createElement('div');
        outer.className = 'flex justify-center rounded-xl border border-slate-200 ' +
            'bg-slate-50 p-2 overflow-auto max-h-[600px] mt-3';
        body.appendChild(outer);

        const pickerWrap = document.createElement('div');
        pickerWrap.className = 'relative inline-block leading-none cursor-crosshair select-none';
        outer.appendChild(pickerWrap);

        const img = document.createElement('img');
        img.src = src; img.alt = 'Spritesheet'; img.draggable = false;
        img.className = 'block max-w-full select-none';
        img.style.maxHeight = '580px';
        pickerWrap.appendChild(img);

        const overlay = document.createElement('div');
        overlay.className = 'absolute inset-0 pointer-events-none';
        overlay.style.backgroundImage =
            `linear-gradient(to right, rgba(59,141,241,0.35) 1px, transparent 1px),
             linear-gradient(to bottom, rgba(59,141,241,0.35) 1px, transparent 1px)`;
        overlay.style.backgroundSize = `${100 / cols}% ${100 / rows}%`;
        pickerWrap.appendChild(overlay);

        const sel = document.createElement('div');
        sel.className = 'absolute pointer-events-none transition-all duration-75';
        sel.style.border = '3px solid #f59e0b';
        sel.style.background = 'rgba(251,191,36,0.18)';
        sel.style.boxShadow = '0 0 0 2px rgba(15,23,42,0.4)';
        sel.style.display = 'none';
        overlay.appendChild(sel);

        const info = document.createElement('div');
        info.className = 'text-xs font-semibold text-amber-700 text-center pt-3';
        info.textContent = 'Click on the image to select cells, then use "From Atlas" on any slot.';
        body.appendChild(info);

        const placeSel = (fc, fr, sc, sr) => {
            sel.style.display = 'block';
            sel.style.left = `${(fc / cols) * 100}%`;
            sel.style.top = `${(fr / rows) * 100}%`;
            sel.style.width = `${(sc / cols) * 100}%`;
            sel.style.height = `${(sr / rows) * 100}%`;
            info.textContent = `Selected: col ${fc}, row ${fr}, span ${sc}x${sr}`;
        };

        let dragStart = null;
        const getCell = (e) => {
            const r = pickerWrap.getBoundingClientRect();
            if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return null;
            return {
                col: Math.max(0, Math.min(cols - 1, Math.floor(((e.clientX - r.left) / r.width) * cols))),
                row: Math.max(0, Math.min(rows - 1, Math.floor(((e.clientY - r.top) / r.height) * rows))),
            };
        };
        const onMove = (e) => {
            if (!dragStart) return;
            const c = getCell(e);
            if (!c) return;
            placeSel(
                Math.min(dragStart.col, c.col), Math.min(dragStart.row, c.row),
                Math.abs(c.col - dragStart.col) + 1, Math.abs(c.row - dragStart.row) + 1,
            );
        };
        const onUp = (e) => {
            if (dragStart) {
                const c = getCell(e) || dragStart;
                const fc = Math.min(dragStart.col, c.col);
                const fr = Math.min(dragStart.row, c.row);
                const sc = Math.abs(c.col - dragStart.col) + 1;
                const sr = Math.abs(c.row - dragStart.row) + 1;
                this._atlasSelection = { frameCol: fc, frameRow: fr, spanCols: sc, spanRows: sr };
            }
            dragStart = null;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        pickerWrap.addEventListener('pointerdown', (e) => {
            const c = getCell(e);
            if (!c) return;
            dragStart = c;
            placeSel(c.col, c.row, 1, 1);
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp, { once: true });
        });

        card.appendChild(body);
        return card;
    }

    _setAtlasGrid(atlasKey, desc, atlas) {
        this.game.theme.setDescriptor(atlasKey, { ...desc, atlas });
    }

    /* ─────────────────────────────────────────────────────────────
       SLOT CARD — single editable image slot
       ───────────────────────────────────────────────────────────── */

    _buildSlotCard(slot, atlasKey) {
        const desc = this.game.theme.getDescriptor?.(slot.key);
        const src = this.game.theme.get(slot.key);
        const hasSkin = !!src;
        const isGrid = desc?.mappingType === 'gridRegion' && desc?.atlas;
        const variations = this.game.theme.getVariations?.(slot.key) || [];
        const userOverride = !!this.game.theme.getAll?.()[slot.key];
        const showVariations = !userOverride && !isGrid && variations.length > 1
            && desc?.assetType !== 'video';

        const card = document.createElement('div');
        card.className = 'rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden ' +
            'shadow-sm hover:shadow-md transition-shadow';

        // Status strip.
        const status = document.createElement('div');
        status.className = `h-1 ${hasSkin ? 'bg-emerald-500' : 'bg-slate-200'}`;
        card.appendChild(status);

        // Preview pane.
        const preview = document.createElement('div');
        preview.className = 'relative flex items-center justify-center h-28 ' +
            'bg-slate-50 border-b border-slate-100';
        if (showVariations) {
            preview.className = 'relative flex items-center h-32 bg-slate-50 ' +
                'border-b border-slate-100 overflow-x-auto';
            this._buildVariationStrip(preview, variations, slot);
        } else if (hasSkin && desc?.assetType === 'video') {
            const vid = document.createElement('video');
            vid.src = src;
            vid.className = 'max-w-full max-h-full object-contain';
            vid.autoplay = true; vid.loop = true; vid.muted = true; vid.playsInline = true;
            preview.appendChild(vid);
        } else if (hasSkin) {
            preview.appendChild(this._buildPreviewEl(src, desc));
        } else {
            const empty = document.createElement('span');
            empty.className = 'text-[11px] font-semibold uppercase tracking-wider text-slate-400';
            empty.textContent = 'No asset';
            preview.appendChild(empty);
        }
        card.appendChild(preview);

        // Body.
        const body = document.createElement('div');
        body.className = 'p-3 space-y-2';
        card.appendChild(body);

        const nameRow = document.createElement('div');
        nameRow.className = 'flex items-center justify-between gap-2';
        const name = document.createElement('span');
        name.className = 'text-sm font-bold text-slate-800 truncate';
        name.textContent = slot.label;
        nameRow.appendChild(name);

        const badges = document.createElement('div');
        badges.className = 'flex items-center gap-1 shrink-0';
        if (slot.footprint) {
            badges.appendChild(this._badge(`${slot.footprint.w}×${slot.footprint.h}`, 'slate'));
        }
        if (slot.supportsAnimation) badges.appendChild(this._badge('Anim', 'indigo'));
        if (slot.supportsDirection) badges.appendChild(this._badge('Dir', 'pink'));
        nameRow.appendChild(badges);
        body.appendChild(nameRow);

        const btns = document.createElement('div');
        btns.className = 'flex flex-wrap gap-1.5';
        body.appendChild(btns);
        btns.appendChild(this._uploadButton(slot));
        if (hasSkin) {
            btns.appendChild(this._secondaryButton('close', 'Clear',
                () => this.game.theme.clear(slot.key)));
        }
        if (atlasKey && slot.key !== atlasKey) {
            const atlasSrc = this.game.theme.get(atlasKey);
            if (atlasSrc) {
                btns.appendChild(this._secondaryButton('grid_view', 'From Atlas', () => {
                    const atlasDesc = this.game.theme.getDescriptor?.(atlasKey) || {};
                    const atlasGrid = atlasDesc.atlas || {};
                    const gridCols = Math.max(1, atlasGrid.gridCols || 4);
                    const gridRows = Math.max(1, atlasGrid.gridRows || 4);
                    const selA = this._atlasSelection;
                    this.game.theme.setDescriptor(slot.key, {
                        ...(this.game.theme.getDescriptor(slot.key) || {}),
                        assetType: 'image', mappingType: 'gridRegion', src: atlasSrc,
                        atlas: {
                            gridCols, gridRows,
                            frameCol: selA ? selA.frameCol : 0,
                            frameRow: selA ? selA.frameRow : 0,
                            spanCols: selA ? selA.spanCols : Math.min(slot.footprint?.w || 1, gridCols),
                            spanRows: selA ? selA.spanRows : Math.min(slot.footprint?.h || 1, gridRows),
                        },
                    });
                }));
            }
        }

        // Inline grid-region picker (only when slot is using a region of an atlas).
        if (hasSkin && isGrid && desc?.assetType !== 'video') {
            body.appendChild(this._buildSlotPicker(slot, desc));
        }

        // Animation / Direction collapsible.
        if (hasSkin && desc?.assetType !== 'video'
            && (slot.supportsAnimation || slot.supportsDirection)) {
            const det = document.createElement('details');
            det.className = 'border-t border-slate-100 pt-2 mt-2';
            const sum = document.createElement('summary');
            sum.className = 'cursor-pointer text-xs font-bold uppercase ' +
                'tracking-wider text-slate-500 select-none list-none';
            sum.textContent = 'Animation / Direction';
            det.appendChild(sum);
            const inner = document.createElement('div');
            inner.className = 'pt-3 space-y-2';
            if (slot.supportsAnimation) inner.appendChild(this._buildAnimPanel(slot, desc));
            if (slot.supportsDirection) inner.appendChild(this._buildDirPanel(slot));
            det.appendChild(inner);
            body.appendChild(det);
        }

        return card;
    }

    /* ─────────────────────────────────────────────────────────────
       VARIATION STRIP — thumbs for slots that ship multiple defaults
       (e.g. vehicle.car ships 4 colour variants).
       ───────────────────────────────────────────────────────────── */

    _buildVariationStrip(previewEl, paths, slot) {
        const wrap = document.createElement('div');
        wrap.className = 'flex items-center gap-2 px-3 py-2 w-full ' +
            'overflow-x-auto justify-center';
        const cellW = paths.length <= 3 ? 68 : (paths.length === 4 ? 56 : 52);
        const cellH = 88;
        for (const path of paths) {
            const cell = document.createElement('div');
            cell.className = 'shrink-0 flex items-center justify-center rounded-lg ' +
                'border border-slate-200 bg-white p-1';
            cell.style.width = `${cellW}px`;
            cell.style.height = `${cellH}px`;
            cell.title = this._basename(path);
            const im = document.createElement('img');
            im.src = path;
            im.alt = this._basename(path);
            im.draggable = false;
            im.className = 'max-w-full max-h-full object-contain';
            cell.appendChild(im);
            wrap.appendChild(cell);
        }
        previewEl.appendChild(wrap);

        const count = document.createElement('span');
        count.className = 'absolute top-2 right-2 text-[10px] font-bold ' +
            'px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200 ' +
            'pointer-events-none';
        count.textContent = `${paths.length}×`;
        count.title = `${paths.length} default variations for ${slot.label}`;
        previewEl.appendChild(count);
    }

    _basename(path) {
        return String(path).split(/[/\\]/).pop().replace(/\.[^.]+$/, '');
    }

    /* ─────────────────────────────────────────────────────────────
       PREVIEW (full image OR cropped atlas region)
       ───────────────────────────────────────────────────────────── */

    _buildPreviewEl(src, desc) {
        if (desc?.mappingType === 'gridRegion' && desc?.atlas) {
            return this._buildCropPreview(src, desc);
        }
        const im = document.createElement('img');
        im.src = src;
        im.className = 'max-w-full max-h-full object-contain';
        return im;
    }

    _buildCropPreview(src, desc) {
        const a = desc.atlas;
        const sc = Math.max(1, Number(a?.spanCols) || 1);
        const sr = Math.max(1, Number(a?.spanRows) || 1);
        const ratio = sc / sr;
        let w = 180, h = Math.round(w / ratio);
        if (h > 90) { h = 90; w = Math.round(h * ratio); }

        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.style.width = `${w}px`; canvas.style.height = `${h}px`;
        canvas.className = 'rounded';
        const ctx = canvas.getContext('2d');
        const im = new Image();
        im.onload = () => {
            const cw = im.naturalWidth / Math.max(1, a.gridCols || 1);
            const ch = im.naturalHeight / Math.max(1, a.gridRows || 1);
            ctx.drawImage(im,
                (a.frameCol || 0) * cw, (a.frameRow || 0) * ch, cw * sc, ch * sr,
                0, 0, w, h);
        };
        im.src = src;
        return canvas;
    }

    /* ─────────────────────────────────────────────────────────────
       SLOT PICKER — drag-select a region of the slot's current atlas
       ───────────────────────────────────────────────────────────── */

    _buildSlotPicker(slot, desc) {
        const atlas = desc.atlas;
        const cols = Math.max(1, atlas.gridCols || 1);
        const rows = Math.max(1, atlas.gridRows || 1);
        const src = this.game.theme.get(slot.key);

        const wrap = document.createElement('div');
        wrap.className = 'pt-2 space-y-2';

        const readout = document.createElement('div');
        readout.className = 'text-[11px] font-semibold text-slate-500';
        readout.textContent = `Grid ${cols}×${rows} | col ${atlas.frameCol}, row ${atlas.frameRow}, span ${atlas.spanCols}×${atlas.spanRows}`;
        wrap.appendChild(readout);

        const outer = document.createElement('div');
        outer.className = 'flex justify-center rounded-lg border border-slate-200 ' +
            'bg-slate-50 p-1 overflow-auto max-h-72';
        wrap.appendChild(outer);

        const pickerWrap = document.createElement('div');
        pickerWrap.className = 'relative inline-block leading-none cursor-crosshair select-none';
        outer.appendChild(pickerWrap);

        const im = document.createElement('img');
        im.src = src; im.alt = slot.label; im.draggable = false;
        im.className = 'block max-w-full select-none';
        im.style.maxHeight = '280px';
        pickerWrap.appendChild(im);

        const overlay = document.createElement('div');
        overlay.className = 'absolute inset-0 pointer-events-none';
        overlay.style.backgroundImage =
            `linear-gradient(to right, rgba(15,23,42,0.2) 1px, transparent 1px),
             linear-gradient(to bottom, rgba(15,23,42,0.2) 1px, transparent 1px)`;
        overlay.style.backgroundSize = `${100 / cols}% ${100 / rows}%`;
        pickerWrap.appendChild(overlay);

        const sel = document.createElement('div');
        sel.className = 'absolute pointer-events-none transition-all duration-75';
        sel.style.border = '3px solid #f59e0b';
        sel.style.background = 'rgba(251,191,36,0.18)';
        overlay.appendChild(sel);

        const placeSel = (fc, fr, sc, sr) => {
            sel.style.left = `${(fc / cols) * 100}%`;
            sel.style.top = `${(fr / rows) * 100}%`;
            sel.style.width = `${(sc / cols) * 100}%`;
            sel.style.height = `${(sr / rows) * 100}%`;
        };
        placeSel(atlas.frameCol || 0, atlas.frameRow || 0,
            Math.max(1, atlas.spanCols || 1), Math.max(1, atlas.spanRows || 1));

        let dragStart = null;
        const getCell = (e) => {
            const r = pickerWrap.getBoundingClientRect();
            if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return null;
            return {
                col: Math.max(0, Math.min(cols - 1, Math.floor(((e.clientX - r.left) / r.width) * cols))),
                row: Math.max(0, Math.min(rows - 1, Math.floor(((e.clientY - r.top) / r.height) * rows))),
            };
        };
        const onMove = (e) => {
            if (!dragStart) return;
            const c = getCell(e);
            if (!c) return;
            placeSel(
                Math.min(dragStart.col, c.col), Math.min(dragStart.row, c.row),
                Math.abs(c.col - dragStart.col) + 1, Math.abs(c.row - dragStart.row) + 1,
            );
        };
        const onUp = (e) => {
            if (!dragStart) return;
            const c = getCell(e) || dragStart;
            const fc = Math.min(dragStart.col, c.col);
            const fr = Math.min(dragStart.row, c.row);
            const sc = Math.abs(c.col - dragStart.col) + 1;
            const sr = Math.abs(c.row - dragStart.row) + 1;
            this.game.theme.setDescriptor(slot.key, {
                ...desc, mappingType: 'gridRegion',
                atlas: { ...atlas, frameCol: fc, frameRow: fr, spanCols: sc, spanRows: sr },
            });
            dragStart = null;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        pickerWrap.addEventListener('pointerdown', (e) => {
            const c = getCell(e);
            if (!c) return;
            dragStart = c;
            placeSel(c.col, c.row, 1, 1);
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp, { once: true });
        });

        // Quick adjust row (C / R / W / H number fields).
        const adjust = document.createElement('div');
        adjust.className = 'flex flex-wrap items-center gap-2 px-3 py-2 ' +
            'rounded-lg bg-slate-50 ring-1 ring-slate-200';
        const adjLbl = document.createElement('span');
        adjLbl.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
        adjLbl.textContent = 'Adjust';
        adjust.appendChild(adjLbl);
        const mini = (label, cur, min, max, fn) => {
            const w = document.createElement('div');
            w.className = 'flex items-center gap-1';
            const l = document.createElement('span');
            l.className = 'text-[11px] font-bold text-slate-500';
            l.textContent = label;
            w.appendChild(l);
            const inp = document.createElement('input');
            inp.type = 'number'; inp.min = String(min); inp.max = String(max); inp.step = '1';
            inp.value = String(cur);
            inp.className = 'w-12 px-2 py-1 text-xs font-semibold text-center ' +
                'rounded-md ring-1 ring-slate-200 bg-white focus:outline-none ' +
                'focus:ring-2 focus:ring-brand-500';
            inp.addEventListener('change', () => {
                fn(Math.max(min, Math.min(max, parseInt(inp.value, 10) || min)));
            });
            w.appendChild(inp);
            return w;
        };
        adjust.appendChild(mini('C', atlas.gridCols, 1, 128, (v) => {
            const next = { ...atlas, gridCols: v };
            if (next.frameCol >= v) next.frameCol = v - 1;
            if (next.spanCols > v) next.spanCols = v;
            this.game.theme.setDescriptor(slot.key, { ...desc, mappingType: 'gridRegion', atlas: next });
        }));
        adjust.appendChild(mini('R', atlas.gridRows, 1, 128, (v) => {
            const next = { ...atlas, gridRows: v };
            if (next.frameRow >= v) next.frameRow = v - 1;
            if (next.spanRows > v) next.spanRows = v;
            this.game.theme.setDescriptor(slot.key, { ...desc, mappingType: 'gridRegion', atlas: next });
        }));
        adjust.appendChild(mini('W', atlas.spanCols, 1, 16, (v) => {
            this.game.theme.setDescriptor(slot.key, { ...desc, mappingType: 'gridRegion',
                atlas: { ...atlas, spanCols: v } });
        }));
        adjust.appendChild(mini('H', atlas.spanRows, 1, 16, (v) => {
            this.game.theme.setDescriptor(slot.key, { ...desc, mappingType: 'gridRegion',
                atlas: { ...atlas, spanRows: v } });
        }));
        wrap.appendChild(adjust);

        return wrap;
    }

    /* ─────────────────────────────────────────────────────────────
       ANIMATION / DIRECTION PANELS
       ───────────────────────────────────────────────────────────── */

    _buildAnimPanel(slot, desc) {
        const anim = desc?.animation || { mode: 'static', fps: 8, startCol: 0, startRow: 0, frames: 1, loop: true };
        const wrap = document.createElement('div');
        wrap.className = 'space-y-2';

        const row = document.createElement('div');
        row.className = 'flex items-center gap-2';
        const l = document.createElement('span');
        l.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
        l.textContent = 'Anim';
        row.appendChild(l);
        row.appendChild(this._chip('Static', anim.mode !== 'frames', () => {
            this.game.theme.setDescriptor(slot.key,
                { ...desc, animation: { ...anim, mode: 'static', frames: 1 } });
        }));
        row.appendChild(this._chip('Frames', anim.mode === 'frames', () => {
            this.game.theme.setDescriptor(slot.key,
                { ...desc, animation: { ...anim, mode: 'frames' } });
        }));
        wrap.appendChild(row);

        if (anim.mode === 'frames') {
            const grid = document.createElement('div');
            grid.className = 'flex flex-wrap items-center gap-2';
            const mk = (label, key, min, max) => {
                const w = document.createElement('div');
                w.className = 'flex items-center gap-1';
                const ll = document.createElement('span');
                ll.className = 'text-[11px] font-bold text-slate-500';
                ll.textContent = label;
                w.appendChild(ll);
                const inp = document.createElement('input');
                inp.type = 'number';
                inp.min = String(min); inp.max = String(max); inp.step = '1';
                inp.value = String(anim[key] ?? min);
                inp.className = 'w-14 px-2 py-1 text-xs font-semibold text-center ' +
                    'rounded-md ring-1 ring-slate-200 bg-white focus:outline-none ' +
                    'focus:ring-2 focus:ring-brand-500';
                inp.addEventListener('change', () => {
                    const v = Math.max(min, Math.min(max, parseInt(inp.value, 10) || min));
                    this.game.theme.setDescriptor(slot.key,
                        { ...desc, animation: { ...anim, [key]: v } });
                });
                w.appendChild(inp);
                return w;
            };
            grid.appendChild(mk('FPS', 'fps', 1, 60));
            grid.appendChild(mk('Start', 'startCol', 0, 63));
            grid.appendChild(mk('Row', 'startRow', 0, 63));
            grid.appendChild(mk('Count', 'frames', 1, 64));
            wrap.appendChild(grid);

            const loopRow = document.createElement('div');
            loopRow.className = 'flex items-center gap-2';
            const loopL = document.createElement('span');
            loopL.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
            loopL.textContent = 'Loop';
            loopRow.appendChild(loopL);
            const loopOn = anim.loop !== false;
            loopRow.appendChild(this._chip(loopOn ? 'On' : 'Off', loopOn, () => {
                this.game.theme.setDescriptor(slot.key,
                    { ...desc, animation: { ...anim, loop: !loopOn } });
            }));
            wrap.appendChild(loopRow);
        }
        return wrap;
    }

    _buildDirPanel(slot) {
        const wrap = document.createElement('div');
        wrap.className = 'flex items-center gap-2';
        const l = document.createElement('span');
        l.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
        l.textContent = 'Facing';
        wrap.appendChild(l);
        const cur = this.game.theme.getSetting(slot.key, 'frontDirection', 'right');
        for (const dir of ['left', 'right']) {
            wrap.appendChild(this._chip(
                dir.charAt(0).toUpperCase() + dir.slice(1),
                cur === dir,
                () => this.game.theme.setSetting(slot.key, 'frontDirection', dir),
            ));
        }
        return wrap;
    }

    /* ─────────────────────────────────────────────────────────────
       PER-GAME SETTINGS BUILDERS — schema-driven; identical wiring
       to the old scene, only the label rendering is different (uses
       Tailwind primitives in _settingField).
       ───────────────────────────────────────────────────────────── */

    _buildTrainsimSettings(panel, key) {
        const schema = (k) => this.game.theme?.getSettingSchema?.('trainsim', k) || {};

        const hint = document.createElement('div');
        hint.className = 'rounded-xl border border-dashed border-slate-200 ' +
            'bg-slate-50 px-4 py-3 text-sm text-slate-600 leading-relaxed';
        hint.innerHTML = `Design new stages with the visual editor. Trains, tracks, signal lamps and switches are placed on a 26×13 grid; the level saves as a JSON file you drop into <code class="font-mono text-brand-700">data/stages/trainsim/</code>.`;
        panel.appendChild(hint);

        const wl = schema('windLeavesEnabled');
        const pb = schema('pauseButtonEnabled');
        const fm = schema('focusModeEnabled');
        const rw = schema('rewindEnabled');
        const sd = schema('startDelaySeconds');
        const tg = document.createElement('div');
        tg.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        tg.appendChild(this._toggle(key, 'windLeavesEnabled',    t('settings.wind_leaves'),     wl.default ?? true));
        tg.appendChild(this._toggle(key, 'pauseButtonEnabled',   t('settings.trainsim_pause'),  pb.default ?? true));
        tg.appendChild(this._toggle(key, 'focusModeEnabled',     t('settings.trainsim_focus'),  fm.default ?? true));
        tg.appendChild(this._toggle(key, 'rewindEnabled',        t('settings.trainsim_rewind'), rw.default ?? true));
        panel.appendChild(tg);

        // Numeric tuning row — kept on its own grid below the toggles
        // so the labels read clearly on phones (1-col) and laptops
        // (2-col). Currently a single field; mirrors the platformer
        // / frogger patterns so adding more numeric trainsim settings
        // here later is a one-line change.
        const ng = document.createElement('div');
        ng.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        ng.appendChild(this._numField(key, 'startDelaySeconds', t('settings.trainsim_start_delay'),
            sd.default ?? 30, sd.min ?? 0, sd.max ?? 120, sd.step ?? 5));
        panel.appendChild(ng);
    }

    _buildPlatformerSettings(panel, key) {
        const schema = (k) => this.game.theme?.getSettingSchema?.('platformer', k) || {};

        const hint = document.createElement('div');
        hint.className = 'rounded-xl border border-dashed border-slate-200 ' +
            'bg-slate-50 px-4 py-3 text-sm text-slate-600 leading-relaxed';
        hint.innerHTML = `Build Mario-style stages with the visual editor — paint terrain, drop enemies, coins and the goal flag, then test-play instantly. Levels save as JSON you drop into <code class="font-mono text-brand-700">data/stages/platformer/</code>.`;
        panel.appendChild(hint);

        const lives = schema('startingLives');
        const jump  = schema('jumpStrength');

        panel.appendChild(this._settingField('Game Mode', 'sports_esports',
            this._segmented([
                { id: 'story', label: 'Story' },
                { id: 'timeTrial', label: 'Time Trial' },
            ], this.game.theme.getSetting(key, 'gameMode', 'story'),
               (id) => this.game.theme.setSetting(key, 'gameMode', id))));
        panel.appendChild(this._settingField('Starting Power', 'bolt',
            this._segmented([
                { id: 'small', label: 'Small' },
                { id: 'super', label: 'Super' },
            ], this.game.theme.getSetting(key, 'startPower', 'small'),
               (id) => this.game.theme.setSetting(key, 'startPower', id))));

        const ng = document.createElement('div');
        ng.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        ng.appendChild(this._numField(key, 'startingLives', 'Starting Lives',
            lives.default ?? 3, lives.min ?? 1, lives.max ?? 9, lives.step ?? 1));
        ng.appendChild(this._numField(key, 'jumpStrength', 'Jump Strength',
            jump.default ?? 15.5, jump.min ?? 11, jump.max ?? 22, jump.step ?? 0.5));
        panel.appendChild(ng);

        const tg = document.createElement('div');
        tg.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        tg.appendChild(this._toggle(key, 'infiniteLives', 'Infinite Lives (Practice)', false));
        panel.appendChild(tg);
    }

    _buildPipelineSettings(panel, key) {
        panel.appendChild(this._settingField('Train Speed', 'speed',
            this._segmented([
                { id: 'slow', label: 'Slow' },
                { id: 'normal', label: 'Normal' },
                { id: 'fast', label: 'Fast' },
            ], this.game.theme.getSetting(key, 'trainSpeed', 'normal'),
               (id) => this.game.theme.setSetting(key, 'trainSpeed', id))));
        panel.appendChild(this._settingField('Train Start Direction', 'navigation',
            this._segmented([
                { id: 'north', label: 'North' }, { id: 'east', label: 'East' },
                { id: 'south', label: 'South' }, { id: 'west', label: 'West' },
            ], this.game.theme.getSetting(key, 'startDirection', 'east'),
               (id) => this.game.theme.setSetting(key, 'startDirection', id))));
        const ng = document.createElement('div');
        ng.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        ng.appendChild(this._numField(key, 'countdownSeconds', 'Countdown (s)', 20, 5, 60, 1));
        ng.appendChild(this._numField(key, 'queueSize', 'Queue Depth', 5, 3, 10, 1));
        panel.appendChild(ng);
    }

    _buildFroggerSettings(panel, key) {
        panel.appendChild(this._settingField(t('settings.game_mode'), 'sports_esports',
            this._segmented([
                { id: 'fixed', label: t('frogger.mode_fixed') },
                { id: 'infinite', label: t('frogger.mode_infinite') },
            ], this.game.theme.getSetting(key, 'gameMode', 'fixed'),
               (id) => this.game.theme.setSetting(key, 'gameMode', id))));
        panel.appendChild(this._settingField(t('frogger.vehicle_speed'), 'speed',
            this._segmented([
                { id: 'slow', label: t('frogger.speed_slow') },
                { id: 'normal', label: t('frogger.speed_normal') },
                { id: 'fast', label: t('frogger.speed_fast') },
            ], this.game.theme.getSetting(key, 'vehicleSpeed', 'normal'),
               (id) => this.game.theme.setSetting(key, 'vehicleSpeed', id))));
        panel.appendChild(this._settingField(t('frogger.vehicle_density'), 'traffic',
            this._segmented([
                { id: 'few', label: t('frogger.density_few') },
                { id: 'normal', label: t('frogger.density_normal') },
                { id: 'many', label: t('frogger.density_many') },
                { id: 'max', label: t('frogger.density_max') },
            ], this.game.theme.getSetting(key, 'vehicleDensity', 'normal'),
               (id) => this.game.theme.setSetting(key, 'vehicleDensity', id))));

        const tg = document.createElement('div');
        tg.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        tg.appendChild(this._toggle(key, 'bonusLives',      t('frogger.bonus_lives'),    true));
        tg.appendChild(this._toggle(key, 'smoothMovement',  t('settings.smooth'),        false));
        tg.appendChild(this._toggle(key, 'balloonTrails',   t('frogger.balloon_trails'), false));
        tg.appendChild(this._toggle(key, 'birdsEnabled',    t('frogger.birds'),          false));
        tg.appendChild(this._toggle(key, 'birdTrails',      t('frogger.bird_trails'),    false));
        tg.appendChild(this._toggle(key, 'abilitiesEnabled',t('frogger.abilities'),      false));
        tg.appendChild(this._toggle(key, 'laneChanging',    t('frogger.lane_changing'),  false));
        tg.appendChild(this._toggle(key, 'timeAttack',      t('frogger.time_attack'),    false));
        panel.appendChild(tg);

        const ng = document.createElement('div');
        ng.className = 'grid grid-cols-2 sm:grid-cols-4 gap-2';
        ng.appendChild(this._numField(key, 'finishScoreGate',  t('frogger.finish_score'),    200, 50, 2000, 50));
        ng.appendChild(this._numField(key, 'timeAttackSeconds',t('frogger.time_attack_sec'), 30,  10, 120,  5));
        ng.appendChild(this._numField(key, 'dashRowsMin',      t('frogger.dash_min'),         3,   1, 30,   1));
        ng.appendChild(this._numField(key, 'dashRowsMax',      t('frogger.dash_max'),        15,   1, 30,   1));
        panel.appendChild(ng);
    }

    _buildSnakeSettings(panel, key) {
        const schema = (k) => this.game.theme?.getSettingSchema?.('snake', k) || {};
        const MODE_LABELS = {
            classic:   'snake.mode_classic',
            adventure: 'snake.mode_adventure',
            stations:  'snake.mode_stations',
            freeroam:  'snake.mode_freeroam',
        };
        const DENSITY_LABELS = {
            few: 'settings.obs_few', normal: 'settings.obs_normal',
            many: 'settings.obs_many', max: 'settings.obs_max',
        };
        const toChoices = (options, labelMap) =>
            (options || []).map((id) => ({ id, label: t(labelMap[id] || id) }));

        const gm = schema('gameMode');
        panel.appendChild(this._settingField(t('settings.game_mode'), 'sports_esports',
            this._segmented(toChoices(gm.options, MODE_LABELS),
                this.game.theme.getSetting(key, 'gameMode', gm.default),
                (id) => this.game.theme.setSetting(key, 'gameMode', id))));

        const od = schema('obstacleDensity');
        panel.appendChild(this._settingField(t('settings.obstacles'), 'park',
            this._segmented(toChoices(od.options, DENSITY_LABELS),
                this.game.theme.getSetting(key, 'obstacleDensity', od.default),
                (id) => this.game.theme.setSetting(key, 'obstacleDensity', id))));

        const sm = schema('smoothMovement');
        const sc = schema('smoothCorners');
        const tt = schema('tramTracks');
        const sd = schema('speedDestroys');
        const tm = schema('touchMode');
        const cs = schema('constantSpeed');
        const cb = schema('cloudBlur');
        const tg = document.createElement('div');
        tg.className = 'grid grid-cols-1 sm:grid-cols-2 gap-2';
        tg.appendChild(this._toggle(key, 'smoothMovement', t('settings.smooth'),         !!sm.default));
        tg.appendChild(this._toggle(key, 'smoothCorners',  t('settings.smooth_corners'), !!sc.default));
        tg.appendChild(this._toggle(key, 'tramTracks',     t('settings.tram_tracks'),    !!tt.default));
        tg.appendChild(this._toggle(key, 'speedDestroys',  t('settings.speed_destroys'), !!sd.default));
        tg.appendChild(this._toggle(key, 'touchMode',      t('settings.touch_mode'),     !!tm.default));
        tg.appendChild(this._toggle(key, 'constantSpeed',  t('settings.constant_speed'), !!cs.default));
        tg.appendChild(this._toggle(key, 'cloudBlur',      t('settings.cloud_blur'),     !!cb.default));
        panel.appendChild(tg);

        const pp = schema('pointsToPass');
        const fr = schema('freeroamTarget');
        const ft = schema('freeroamTimer');
        const ng = document.createElement('div');
        ng.className = 'grid grid-cols-1 sm:grid-cols-3 gap-2';
        ng.appendChild(this._numField(key, 'pointsToPass',   t('settings.points_to_pass'),  pp.default, pp.min, pp.max, pp.step));
        ng.appendChild(this._numField(key, 'freeroamTarget', t('settings.freeroam_target'), fr.default, fr.min, fr.max, fr.step));
        ng.appendChild(this._numField(key, 'freeroamTimer',  t('settings.freeroam_timer'),  ft.default, ft.min, ft.max, ft.step));
        panel.appendChild(ng);

        // Perk timing + lives — added per client direction so each
        // run can be tuned without touching SNAKE_CONFIG. Defaults
        // match the doubled values in `data/snake.config.js`.
        const pls = schema('perkLifetimeSeconds');
        const pcs = schema('perkCountdownSeconds');
        const lps = schema('livesPerStage');
        const inv = schema('invincibleSeconds');
        const ng2 = document.createElement('div');
        ng2.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2';
        ng2.appendChild(this._numField(key, 'perkLifetimeSeconds',  t('snake.perk_lifetime_sec'),  pls.default, pls.min, pls.max, pls.step));
        ng2.appendChild(this._numField(key, 'perkCountdownSeconds', t('snake.perk_countdown_sec'), pcs.default, pcs.min, pcs.max, pcs.step));
        ng2.appendChild(this._numField(key, 'livesPerStage',        t('snake.lives_per_stage'),    lps.default, lps.min, lps.max, lps.step));
        ng2.appendChild(this._numField(key, 'invincibleSeconds',    t('snake.invincible_seconds'), inv.default, inv.min, inv.max, inv.step));
        panel.appendChild(ng2);
    }

    /* ─────────────────────────────────────────────────────────────
       FORM PRIMITIVES — Tailwind controls used everywhere
       ───────────────────────────────────────────────────────────── */

    _settingField(label, icon, control) {
        const row = document.createElement('div');
        row.className = 'rounded-xl ring-1 ring-slate-200 bg-white px-4 py-3 space-y-2';
        const head = document.createElement('div');
        head.className = 'flex items-center gap-2';
        head.innerHTML = `
            <span class="material-symbols-rounded text-slate-400">${icon}</span>
            <span class="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                ${this._esc(label)}
            </span>
        `;
        row.appendChild(head);
        row.appendChild(control);
        return row;
    }

    /** Pill-row segmented control. */
    _segmented(options, current, onChange) {
        const bar = document.createElement('div');
        bar.className = 'inline-flex flex-wrap gap-1.5 p-1.5 rounded-xl bg-slate-100 ' +
            'ring-1 ring-slate-200';
        const btns = [];
        const apply = (id) => {
            for (const b of btns) {
                const isOn = b.dataset.id === id;
                b.className = isOn
                    ? 'px-4 py-2 rounded-lg text-sm font-bold bg-brand-600 text-white ' +
                      'shadow-sm transition-all'
                    : 'px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 ' +
                      'hover:bg-white hover:text-slate-800 hover:shadow-sm transition-all';
            }
        };
        for (const opt of options) {
            const b = document.createElement('button');
            b.type = 'button';
            b.dataset.id = opt.id;
            b.textContent = opt.label;
            b.addEventListener('click', () => {
                onChange(opt.id);
                apply(opt.id);
            });
            btns.push(b);
            bar.appendChild(b);
        }
        apply(current);
        return bar;
    }

    /** Toggle with optional callback (after persistence) for dependent UI. */
    _toggleCallback(slotKey, settingKey, label, defaultVal, onChange) {
        return this._toggleImpl(slotKey, settingKey, label, defaultVal, onChange);
    }
    _toggle(slotKey, settingKey, label, defaultVal) {
        return this._toggleImpl(slotKey, settingKey, label, defaultVal, null);
    }
    _toggleImpl(slotKey, settingKey, label, defaultVal, onChange) {
        const row = document.createElement('div');
        row.className = 'flex items-center justify-between gap-3 px-3 py-2.5 ' +
            'rounded-lg ring-1 ring-slate-200 bg-white';
        const lbl = document.createElement('span');
        lbl.className = 'text-sm font-semibold text-slate-700';
        lbl.textContent = label;
        row.appendChild(lbl);

        const track = document.createElement('button');
        track.type = 'button';
        track.className = 'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors';
        const thumb = document.createElement('span');
        thumb.className = 'absolute top-0.5 inline-block h-5 w-5 rounded-full ' +
            'bg-white shadow ring-1 ring-slate-300 transition-transform';
        track.appendChild(thumb);

        const apply = (v) => {
            track.style.backgroundColor = v ? '#22c55e' : '#cbd5e1';
            thumb.style.transform = v ? 'translateX(22px)' : 'translateX(2px)';
        };
        apply(!!this.game.theme.getSetting(slotKey, settingKey, defaultVal));
        track.addEventListener('click', () => {
            const next = !this.game.theme.getSetting(slotKey, settingKey, defaultVal);
            this.game.theme.setSetting(slotKey, settingKey, next);
            apply(next);
            if (typeof onChange === 'function') onChange();
        });
        row.appendChild(track);
        return row;
    }

    /** Range slider with live readout + reset-to-default. */
    _slider(slotKey, settingKey, label, defaultVal, min, max, step) {
        const row = document.createElement('div');
        row.className = 'rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2 space-y-1.5';

        const decimals = (step >= 1) ? 0 : (String(step).split('.')[1] || '').length;
        const fmt = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n.toFixed(decimals) : String(defaultVal);
        };
        const clamp = (v) => Math.max(min, Math.min(max, v));

        const head = document.createElement('div');
        head.className = 'flex items-center justify-between gap-2';

        const lbl = document.createElement('span');
        lbl.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
        lbl.textContent = label;
        head.appendChild(lbl);

        const right = document.createElement('div');
        right.className = 'flex items-center gap-1';
        const reset = document.createElement('button');
        reset.type = 'button';
        reset.title = 'Reset to default';
        reset.className = 'p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100';
        reset.innerHTML = `<span class="material-symbols-rounded" style="font-size:14px">restart_alt</span>`;
        right.appendChild(reset);

        const valueEl = document.createElement('span');
        valueEl.className = 'text-[11px] font-bold text-slate-700 tabular-nums ' +
            'px-2 py-0.5 rounded bg-slate-100 ring-1 ring-slate-200 min-w-[40px] text-right';
        right.appendChild(valueEl);
        head.appendChild(right);
        row.appendChild(head);

        const inp = document.createElement('input');
        inp.type = 'range';
        inp.min = String(min); inp.max = String(max); inp.step = String(step);
        inp.className = 'w-full';

        const initial = clamp(Number(this.game.theme.getSetting(slotKey, settingKey, defaultVal)));
        inp.value = String(initial);
        valueEl.textContent = fmt(initial);

        inp.addEventListener('input', () => {
            const v = clamp(Number(inp.value));
            valueEl.textContent = fmt(v);
            this.game.theme.setSetting(slotKey, settingKey, v);
        });
        reset.addEventListener('click', () => {
            inp.value = String(defaultVal);
            valueEl.textContent = fmt(defaultVal);
            this.game.theme.setSetting(slotKey, settingKey, defaultVal);
        });

        row.appendChild(inp);
        return row;
    }

    /** Boxed number input with label above. */
    _numField(slotKey, settingKey, label, defaultVal, min, max, step) {
        const box = document.createElement('div');
        box.className = 'flex flex-col items-center gap-1 rounded-lg ring-1 ring-slate-200 ' +
            'bg-white px-3 py-2';
        const lbl = document.createElement('span');
        lbl.className = 'text-[11px] font-semibold text-slate-500 text-center';
        lbl.textContent = label;
        box.appendChild(lbl);

        const inp = document.createElement('input');
        inp.type = 'number';
        inp.min = String(min); inp.max = String(max); inp.step = String(step);
        inp.value = String(this.game.theme.getSetting(slotKey, settingKey, defaultVal));
        inp.className = 'w-20 px-2 py-1 text-sm font-bold text-center rounded-md ' +
            'ring-1 ring-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500';
        inp.addEventListener('change', () => {
            const v = Math.max(min, Math.min(max, parseFloat(inp.value) || defaultVal));
            inp.value = String(v);
            this.game.theme.setSetting(slotKey, settingKey, v);
        });
        box.appendChild(inp);
        return box;
    }

    /** Stepper widget for the atlas grid card (label + - / value / +). */
    _stepper(label, value, min, max, onChange) {
        const wrap = document.createElement('div');
        wrap.className = 'flex items-center gap-2';
        const l = document.createElement('span');
        l.className = 'text-[11px] font-bold uppercase tracking-wider text-slate-500';
        l.textContent = label;
        wrap.appendChild(l);
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'w-7 h-7 rounded-md ring-1 ring-slate-200 bg-white hover:bg-slate-50 ' +
            'text-slate-700 font-bold';
        minus.textContent = '−';
        wrap.appendChild(minus);
        const display = document.createElement('span');
        display.className = 'min-w-[28px] text-center text-sm font-bold text-slate-800';
        display.textContent = String(value);
        wrap.appendChild(display);
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'w-7 h-7 rounded-md ring-1 ring-slate-200 bg-white hover:bg-slate-50 ' +
            'text-slate-700 font-bold';
        plus.textContent = '+';
        wrap.appendChild(plus);
        minus.addEventListener('click', () => {
            const next = Math.max(min, value - 1);
            if (next !== value) onChange(next);
        });
        plus.addEventListener('click', () => {
            const next = Math.min(max, value + 1);
            if (next !== value) onChange(next);
        });
        return wrap;
    }

    _chip(label, active, fn) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = active
            ? 'px-3 py-1 rounded-md text-[11px] font-bold uppercase ' +
              'tracking-wider bg-brand-600 text-white shadow-sm'
            : 'px-3 py-1 rounded-md text-[11px] font-bold uppercase ' +
              'tracking-wider bg-slate-100 text-slate-600 hover:bg-slate-200';
        b.textContent = label;
        b.addEventListener('click', fn);
        return b;
    }

    _badge(text, variant) {
        const b = document.createElement('span');
        const colorClass = {
            slate:  'bg-slate-100 text-slate-600',
            indigo: 'bg-indigo-100 text-indigo-700',
            pink:   'bg-pink-100 text-pink-700',
            amber:  'bg-amber-100 text-amber-700',
        }[variant] || 'bg-slate-100 text-slate-600';
        b.className = `inline-block text-[10px] font-bold uppercase tracking-wider ` +
            `px-1.5 py-0.5 rounded ${colorClass}`;
        b.textContent = text;
        return b;
    }

    /* ─────────────────────────────────────────────────────────────
       BUTTONS
       ───────────────────────────────────────────────────────────── */

    _buttonClass(variant) {
        const base = 'inline-flex items-center gap-2 px-3.5 py-2 rounded-lg ' +
            'text-sm font-semibold transition-colors select-none';
        switch (variant) {
            case 'primary':
                return base + ' bg-brand-600 text-white hover:bg-brand-700 shadow-sm';
            case 'secondary':
                return base + ' bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50';
            case 'ghost':
                return base + ' text-slate-600 hover:bg-slate-100';
            case 'danger':
                return base + ' bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100';
            default:
                return base;
        }
    }
    _primaryButton(icon, label, fn) { return this._mkButton('primary', icon, label, fn); }
    _secondaryButton(icon, label, fn) { return this._mkButton('secondary', icon, label, fn); }
    _ghostButton(icon, label, fn) { return this._mkButton('ghost', icon, label, fn); }
    _dangerButton(icon, label, fn) { return this._mkButton('danger', icon, label, fn); }
    _mkButton(variant, icon, label, fn) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = this._buttonClass(variant);
        b.innerHTML = `
            <span class="material-symbols-rounded">${icon}</span>
            <span>${this._esc(label)}</span>
        `;
        b.addEventListener('click', fn);
        return b;
    }

    /** File-input wrapped in a label so it inherits all button styles. */
    _uploadButton(slot) {
        const label = document.createElement('label');
        label.className = this._buttonClass('secondary');
        label.style.cursor = 'pointer';
        label.innerHTML = `
            <span class="material-symbols-rounded">upload</span>
            <span>Upload</span>
        `;
        const inp = document.createElement('input');
        inp.type = 'file';
        inp.accept = 'image/png,image/webp,image/jpeg,image/gif,video/mp4,video/webm';
        inp.style.display = 'none';
        inp.addEventListener('change', async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            label.style.opacity = '0.5'; label.style.pointerEvents = 'none';
            try { await this.game.theme.uploadSkin(slot.key, file); }
            catch (err) { console.error('[Options] Upload failed:', err); }
            finally { label.style.opacity = ''; label.style.pointerEvents = ''; }
        });
        label.appendChild(inp);
        return label;
    }

    /* ─────────────────────────────────────────────────────────────
       CARDS / SECTION HEADERS
       ───────────────────────────────────────────────────────────── */

    _pageHeader(title, subtitle, icon) {
        const head = document.createElement('div');
        head.className = 'flex items-start gap-3 pb-2';
        head.innerHTML = `
            <span class="material-symbols-rounded text-brand-600 mt-0.5" style="font-size:30px">${icon}</span>
            <div class="flex-1 min-w-0">
                <h1 class="text-2xl font-bold tracking-tight text-slate-900">
                    ${this._esc(title)}
                </h1>
                <p class="text-sm text-slate-500 mt-0.5">${this._esc(subtitle)}</p>
            </div>
        `;
        return head;
    }

    _card() {
        const c = document.createElement('section');
        c.className = 'rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden';
        return c;
    }
    _cardHeader(icon, title, subtitle) {
        const h = document.createElement('div');
        h.className = 'flex items-start gap-3 px-5 pt-4';
        h.innerHTML = `
            <span class="material-symbols-rounded text-brand-600 mt-0.5">${icon}</span>
            <div class="flex-1 min-w-0">
                <div class="text-sm font-bold text-slate-800">${this._esc(title)}</div>
                ${subtitle ? `<div class="text-xs text-slate-500 mt-0.5">${this._esc(subtitle)}</div>` : ''}
            </div>
        `;
        return h;
    }
    _cardBody() {
        const b = document.createElement('div');
        b.className = 'p-5 space-y-3';
        return b;
    }

    /* ─────────────────────────────────────────────────────────────
       TOAST (replaces window.alert for non-blocking feedback)
       ───────────────────────────────────────────────────────────── */

    _toast(message, variant = 'success') {
        if (!this._toastEl) return;
        const palette = variant === 'error'
            ? { bg: 'bg-rose-50',    fg: 'text-rose-800',    ring: 'ring-rose-200',    icon: 'error'         }
            : variant === 'info'
                ? { bg: 'bg-amber-50',   fg: 'text-amber-800',   ring: 'ring-amber-200',   icon: 'info'          }
                : { bg: 'bg-emerald-50', fg: 'text-emerald-800', ring: 'ring-emerald-200', icon: 'check_circle'  };
        const el = document.createElement('div');
        el.className = `opt-toast pointer-events-auto rounded-xl px-4 py-3 mb-2 ` +
            `${palette.bg} ${palette.fg} ring-1 ${palette.ring} ` +
            `shadow-lg flex items-start gap-2 max-w-sm`;
        el.innerHTML = `
            <span class="material-symbols-rounded mt-0.5">${palette.icon}</span>
            <span class="text-sm font-semibold leading-snug">${this._esc(message)}</span>
        `;
        this._toastEl.appendChild(el);
        window.setTimeout(() => {
            el.style.transition = 'opacity 0.3s';
            el.style.opacity = '0';
            window.setTimeout(() => el.remove(), 320);
        }, 2400);
    }

    /* ─────────────────────────────────────────────────────────────
       SAVE / EXPORT / IMPORT / RESET
       ───────────────────────────────────────────────────────────── */

    _buildPresetSource() {
        const data = this.game.theme.exportAllData();
        const payload = {
            mode: 'soft',
            theme:    data.theme    || {},
            settings: data.settings || {},
            lang:     data.lang     || null,
        };
        const generated = new Date().toISOString();
        const body = JSON.stringify(payload, null, 2);
        return `/**
 * Auto-generated preset bundle.
 *
 * Generated: ${generated}
 *
 * Loaded before the main bundle so the saved theme + settings are baked
 * into every build (\`npm run build\` and \`npm run build:offline\`).
 * Visitors with empty localStorage are seeded with these values;
 * visitors who've already tweaked settings keep their own (soft mode).
 *
 * To force these values on every visitor on every load (e.g. for a
 * kiosk or showcase build), change \`mode: 'soft'\` to \`mode: 'hard'\`
 * below.
 */
window.__GAME_PRESET__ = ${body};
`;
    }

    /** Trigger a browser download of the given preset.js source. */
    _downloadPreset(js) {
        const blob = new Blob([js], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'preset.js';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    _exportConfig() {
        const data = this.game.theme.exportAllData();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'config.json';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this._toast(t('theme.export_done'));
    }

    _exportPreset() {
        this._downloadPreset(this._buildPresetSource());
        this._toast(t('theme.export_preset_done'));
    }

    /**
     * Primary action: POST the preset to the dev server, which writes
     * `data/preset.js`. The next build bakes the values in. No file
     * download — the whole point is the user shouldn't have to manage
     * files at all.
     */
    async _savePreset() {
        const js = this._buildPresetSource();
        try {
            const res = await fetch('/__save-preset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/javascript' },
                body: js,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            this._toast(t('theme.saved_toast'));
        } catch (err) {
            // Dev server isn't running (or page was opened via file://).
            // We can't write disk from the browser, so fall back to a
            // download and tell the user clearly what happened.
            console.warn('[Options] direct preset save failed, downloading instead:', err);
            this._downloadPreset(js);
            this._toast(t('theme.save_failed_toast'), 'info');
        }
    }

    async _importConfig(event) {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const data = JSON.parse(await file.text());
            if (data.theme && typeof data.theme === 'object') {
                for (const [k, v] of Object.entries(data.theme)) this.game.theme.set(k, v);
            }
            if (data.settings && typeof data.settings === 'object') {
                for (const [sk, settings] of Object.entries(data.settings)) {
                    if (typeof settings === 'object') {
                        for (const [k, v] of Object.entries(settings)) {
                            this.game.theme.setSetting(sk, k, v);
                        }
                    }
                }
            }
            if (data.lang) this.game.theme.setLang(data.lang);
            this._renderContent();
            this._toast('Imported');
        } catch (err) {
            console.error('[Options] Import failed:', err);
            this._toast('Import failed: ' + err.message, 'error');
        }
    }

    /* ─────────────────────────────────────────────────────────────
       STAGE EDITOR LAUNCH
       ───────────────────────────────────────────────────────────── */

    _launchStageEditor(targetGame) {
        this.game.editorReturnScene = 'options';
        this.game.editorTargetGame = targetGame;
        this.game.sceneManager.switchScene('stage-editor');
    }

    /* ─────────────────────────────────────────────────────────────
       UTILS
       ───────────────────────────────────────────────────────────── */

    _esc(s) {
        return String(s ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
}
